﻿import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '@/styles/SipaStyles.scss';
import TemplateRenderer from '@/components/ActionItems/TemplateRenderer';
import useSipaSignalR from '@/hooks/useSipaSignalR'; // SignalR for real-time collaboration
import useActionItemSignalR from '@/hooks/useActionItemSignalR'; // SignalR for action item updates
import * as Sentry from '@sentry/react';

// ------------------------------ Local Storage Keys ------------------------------
const LS_KEY_STRUCTURE = (id) => `sipa_report_${id}_structure_v1`;
const LS_KEY_ANSWERS = (id) => `sipa_report_${id}_answers_v1`;
const LS_KEY_QUEUE = 'sipa_report_pending_queue_v2'; // v2: delta-based queue
const LS_KEY_ENFORCED = (id) => `sipa_report_${id}_enforced_subsections_v1`;
const LS_KEY_DIRTY = (id) => `sipa_report_${id}_dirty_controls_v1`; // tracks unsynced controlIds

// ------------------------------ Hooks / Utils ------------------------------
const useDebounce = (fn, delay) => {
  const ref = useRef();
  return useCallback((...args) => {
    clearTimeout(ref.current);
    ref.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
};

// ------------------------------ Option Sanitizing ------------------------------
const sanitizeControlOptions = (ctrl) => {
  if (!ctrl || !Array.isArray(ctrl.Options)) return ctrl;
  
  const seen = new Set();
  const filtered = [];
  
  ctrl.Options.forEach(o => {
    const active = (o.Active !== false) && (o.active !== false);
    if (!active) return;
    
    const val = o.Value ?? o.value;
    if (seen.has(val)) return;
    
    seen.add(val);
    filtered.push(o);
  });
  
  filtered.sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));
  return { ...ctrl, Options: filtered };
};

const sanitizeStructureTree = (root) => {
  if (!root) return root;

  // helper to get order value from different naming conventions
  const getOrder = (obj) => (obj?.Order ?? obj?.order ?? 0);

  return {
    ...root,
    Structure: (root.Structure || []).map(cat => {
      // map sections/subsections/controls and then sort by Order
      const mappedSections = (cat.Sections || []).map(sec => {
        const mappedSubSections = (sec.SubSections || []).map(sub => ({
          ...sub,
          Controls: (sub.Controls || []).map(ctrl => sanitizeControlOptions(ctrl))
        })).sort((a, b) => getOrder(a) - getOrder(b));
        
        return {
          ...sec,
          SubSections: mappedSubSections
        };
      }).sort((a, b) => getOrder(a) - getOrder(b));
      
      return {
        ...cat,
        Sections: mappedSections
      };
    }).map(x => x) // no-op to keep shape
  };
};

// ------------------------------ Scoring ------------------------------
// Returns a percentage [0-100] or null for each control.
// All control types produce a normalised percentage so that section averages
// and weighted category / overall scores are computed consistently.
const scoreControl = (ctrl) => {
  if (!ctrl) return null;

  const val = ctrl.value;
  if (val === undefined || val === null || val === '' || val === 'NA') return null;
  if (ctrl.ExcludeFromScore) return null;

  const max = ctrl.MaxScore || 100;

  switch (ctrl.Type) {
    case 'Checkbox':
      return val === 'true' ? 100 : 0;

    case 'Number': {
      const v = parseFloat(val);
      if (isNaN(v)) return 0;
      return Math.round(Math.max(0, Math.min(100, (v / max) * 100)) * 100) / 100;
    }

    case 'Dropdown':
    case 'RadioGroup': {
      // Value-based scoring: each option has a numeric Value assigned in the designer.
      // Score% = (selectedOptionValue / maxOptionValue) × 100.
      // This respects the designer's intent where e.g. Value=1 (Very Poor) scores 20%
      // and Value=5 (Excellent) scores 100% on a 1-5 scale.
      if (!ctrl.Options || !ctrl.Options.length) return null;

      const activeOptions = ctrl.Options.filter(o => (o.Value ?? '').toUpperCase() !== 'NA');
      if (!activeOptions.length) return null;

      // Parse all option values as numbers
      const numericValues = activeOptions.map(o => parseFloat(o.Value)).filter(v => !isNaN(v));

      if (numericValues.length > 0 && numericValues.length === activeOptions.length) {
        // All options have numeric values — use value-based scoring
        const selectedNum = parseFloat(val);
        if (isNaN(selectedNum)) return null;
        const maxVal = Math.max(...numericValues);
        if (maxVal <= 0) return null;
        return Math.round(Math.max(0, Math.min(100, (selectedNum / maxVal) * 100)) * 100) / 100;
      }

      // Fallback: position-based scoring when option values aren't all numeric
      const ordered = [...ctrl.Options].sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));
      const idx = ordered.findIndex(o => o.Value === val);
      if (idx === -1) return null;
      if (ordered.length === 1) return 100;
      return Math.round((1 - idx / (ordered.length - 1)) * 100 * 100) / 100;
    }

    default:
      // Non-scoring types (TextMultiLine, FileUpload, PhotoCapture, Date)
      return null;
  }
};

const calcSectionStats = (section) => {
  const collected = [];
  
  (section.SubSections || []).forEach(sub => {
    const ctrls = sub.Controls || [];
    if (ctrls.some(c => c.value === 'NA')) return;
    
    ctrls.forEach(c => {
      const sc = scoreControl(c);
      if (sc !== null) collected.push(sc);
    });
  });
  
  if (!collected.length) return { avg: null };
  
  const sum = collected.reduce((a, b) => a + b, 0);
  const avg = (sum / collected.length).toFixed(2);
  return { avg };
};

const isSectionFullyNA = (section) => {
  const subs = section.SubSections || [];
  if (!subs.length) return false;
  
  return subs.every(sub => (sub.Controls || []).some(c => c.value === 'NA'));
};

// ------------------------------ Adapt Server Structure ------------------------------
// Helper: Transform server option to client option
const transformServerOption = (o) => ({
  Id: o.id,
  Text: o.text,
  Value: o.value,
  SortOrder: o.sortOrder ?? 0,
  EnforceActionItem: o.enforceActionItem ?? false,
  Active: o.active !== false
});

// Helper: Transform server control to client control
const transformServerControl = (ctrl) => {
  const baseControl = {
    Id: ctrl.id,
    Label: ctrl.label,
    Type: ctrl.type,
    MaxScore: ctrl.maxScore ?? 100,
    ExcludeFromScore: ctrl.excludeFromScore ?? false,
    AllowNotApplicable: ctrl.allowNotApplicable ?? false,
    EnforceActionItemOnAnySelection: ctrl.enforceActionItemOnAnySelection ?? false,
    value: ctrl.value ?? null,
    AnswerUpdatedUtc: ctrl.answerUpdatedUtc ?? null,
    Options: (ctrl.options || [])
      .filter(o => o.active !== false)
      .map(transformServerOption)
  };

  return sanitizeControlOptions(baseControl);
};

// Helper: Transform server subsection to client subsection
const transformServerSubSection = (sub) => ({
  Id: sub.id,
  Title: sub.title,
  Order: sub.order,
  Weight: sub.weight ?? 0,
  Controls: (sub.controls || []).map(transformServerControl)
});

// Helper: Transform server section to client section
const transformServerSection = (sec) => ({
  Id: sec.id,
  Title: sec.title,
  Order: sec.order,
  Weight: sec.weight ?? 0,
  SubSections: (sec.subSections || []).map(transformServerSubSection)
});

// Helper: Transform server category to client category
const transformServerCategory = (cat) => ({
  CategoryName: cat.category,
  Sections: (cat.sections || []).map(transformServerSection)
});

// Main adapter function
const adaptServerStructure = (raw) => {
  if (!raw || !raw.structure) return raw;
  
  const adapted = {
    ...raw,
    Structure: raw.structure.map(transformServerCategory)
  };
  
  return sanitizeStructureTree(adapted);
};

// ------------------------------ Traversal ------------------------------
const traverseControls = (structure, cb) => {
  (structure?.Structure || []).forEach(cat =>
    (cat.Sections || []).forEach(sec =>
      (sec.SubSections || []).forEach(sub =>
        (sub.Controls || []).forEach(ctrl =>
          cb(ctrl, sub, sec)
        )
      )
    )
  );
};

// ------------------------------ Enforcement Recompute ------------------------------
// Determines which subsections currently require an action item based on control selection.
// A subsection is enforced if any control has EnforceActionItemOnAnySelection and a non-empty/non-NA value
// OR if the selected option within a control has EnforceActionItem = true.
const recomputeEnforcedSubSections = (struct) => {
  const enforced = new Set();
  (struct?.Structure || []).forEach(cat =>
    (cat.Sections || []).forEach(sec =>
      (sec.SubSections || []).forEach(sub => {
        let requires = false;
        (sub.Controls || []).forEach(ctrl => {
          if (requires) return; // already determined
          const val = ctrl.value;
          if (!val || val === 'NA') return;
          if (ctrl.EnforceActionItemOnAnySelection) {
            requires = true;
            return;
          }
          const opt = (ctrl.Options || []).find(o => o.Value === val);
            if (opt && opt.EnforceActionItem) {
              requires = true;
              return;
            }
        });
        if (requires) enforced.add(sub.Id);
      })
    )
  );
  return enforced;
};

const SipaReportCapturePage = () => {
  const { apiFetch, user } = useAuth();
  const { reportId: routeReportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Report Id resolution
  const getQueryIdFromLocation = (location) => {
    if (!location.search) return null;
    const raw = location.search.substring(1);
    if (!raw) return null;
    if (raw.includes('=')) {
      const p = new URLSearchParams(location.search);
      return p.get('id') || p.get('reportId') || null;
    } else {
      return raw;
    }
  };
  const queryId = getQueryIdFromLocation(location);
  const reportId = routeReportId || queryId;

  // ------------------------------ State ------------------------------
  const [structure, setStructure] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [status, setStatus] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // Initialise from localStorage so the mount effect never calls setPendingSync synchronously
  const [pendingSync, setPendingSync] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY_QUEUE) || '[]').length > 0; } catch { return false; }
  });
  const [autoSaveTS, setAutoSaveTS] = useState(null);
  const [actionCounts, setActionCounts] = useState({});
  const [enforcedSubSections, setEnforcedSubSections] = useState(new Set());
  const [highlightSubs, setHighlightSubs] = useState(new Set()); // Track subsections to highlight
  const [errorMessage, setErrorMessage] = useState(''); // Non-blocking error messages for user feedback
  const [setTotalStats] = useState(null); // Server total stats for the report

  // Sign-off
  const [signOffModalOpen, setSignOffModalOpen] = useState(false);
  const [signOffAgree, setSignOffAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signOffError, setSignOffError] = useState('');
  // Workflow permissions (fetched from SipaWorkflowConfig)
  const [workflowPerms, setWorkflowPerms] = useState({ canPrimarySignoff: false, canSecondarySignoff: false, canOverrideValidation: false, canSignoff: false });
  // Revoke sign-off
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [revokeStage, setRevokeStage] = useState(''); // 'primary' | 'final'
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState('');

  // Action Items
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiTarget, setAiTarget] = useState(null);
  const [aiForm, setAiForm] = useState({ description: '', priority: 'Low', comments: '', assignedToUserId: null });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiListModalOpen, setAiListModalOpen] = useState(false);
  const [aiListTarget, setAiListTarget] = useState(null);
  const [aiListItems, setAiListItems] = useState([]);
  const [aiListLoading, setAiListLoading] = useState(false);
  // NEW: user search state for Assign To dropdown
  const [aiUserSearch, setAiUserSearch] = useState('');
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const assignToRef = useRef(null);

  // Users (lazy)
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Photo modal
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalSrc, setPhotoModalSrc] = useState(null);

  // Template modal
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [contextTemplate, setContextTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);

  // Settings
  const applyEnf = false; const reScore = true;

  // ------------------------------ Header Derivation ------------------------------
  const propertyName = reportMeta?.propertyName || reportMeta?.PropertyName || structure?.PropertyName || structure?.propertyName || reportMeta?.siteName || reportMeta?.SiteName || 'Unknown Property';
  const deriveMonthYear = () => { const dStr = reportMeta?.periodStart || reportMeta?.PeriodStart || structure?.periodStart || structure?.PeriodStart; if (dStr) { const d = new Date(dStr); if (!isNaN(d.getTime())) return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); } const label = reportMeta?.periodLabel || reportMeta?.PeriodLabel || structure?.periodLabel || structure?.PeriodLabel || ''; if (/^[A-Za-z]+\s+\d{4}$/.test(label.trim())) return label.trim(); const match = label.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}/i); if (match) return match[0]; return new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); };
  const headerLabel = `Site Assessment: ${propertyName} - ${deriveMonthYear()}`;

  // ------------------------------ Role Detection ------------------------------
  // roleName is provided directly by the login response (UserDetails.RoleName → stored in user object).
  // We no longer rely on scanning string values — that approach was fragile and broke sign-off.
  const userRoleDisplay = user?.roleName || user?.RoleName || '';

  // ReadOnly detection: check common shapes where a ReadOnly flag may live on the user/permissions object.
  // The Sign Off button is now visible to everyone unless ReadOnly === true.
  const isReadOnly = Boolean(
    user?.ReadOnly ?? user?.readOnly ??
    user?.permissions?.ReadOnly ?? user?.permissions?.readOnly ??
    user?.Permissions?.ReadOnly ?? user?.Permissions?.readOnly ??
    user?.claims?.ReadOnly ?? user?.claims?.readOnly
  );

  // Final decision: allow final sign-off UI only when the user is not readonly
  // AND the workflow permissions confirm they have a sign-off role.
  const canFinalSignOff = !isReadOnly && workflowPerms.canSignoff;

  // Whether this user can override validation (from workflow config)
  const canOverrideValidation = workflowPerms.canOverrideValidation;

  // ------------------------------ Locking ------------------------------
  const finalSigned = reportMeta?.finalSigned || reportMeta?.FinalSigned || false;
  const centreManagerSigned = reportMeta?.centreManagerSigned || reportMeta?.CentreManagerSigned || false;
  // After primary sign-off: primary signers are locked out; secondary/override can still edit.
  // After final sign-off: everyone is locked out.
  const isPrimarySigner = workflowPerms.canPrimarySignoff && !workflowPerms.canSecondarySignoff && !workflowPerms.canOverrideValidation;
  const editingDisabled = isReadOnly || finalSigned || (centreManagerSigned && isPrimarySigner);

  // ------------------------------ SignalR Real-Time Collaboration ------------------------------
  // Callbacks for SignalR events
  const handleAnswersUpdated = useCallback((update) => {
    console.log('[SignalR SIPA] Received answer update:', update);
    
    // Only process updates from other users (not our own)
    const currentUserId = user?.userID || user?.id || user?.Id;
    if (update.ChangedByUserId === currentUserId?.toString() || update.changedByUserId === currentUserId?.toString()) {
      console.log('[SignalR SIPA] Ignoring own update');
      return; // Ignore our own updates
    }

    // Get answers array (handle both camelCase and PascalCase)
    const answers = update.Answers || update.answers;
    
    // Update local structure with new answers from other user
    if (!answers || !Array.isArray(answers)) {
      console.log('[SignalR SIPA] Invalid update data - no answers array. Update:', update);
      return;
    }

    // Read the LATEST structure from the ref. Using the closure variable here
    // is unsafe: a SignalR broadcast that arrives during the same render cycle
    // as a local edit would otherwise be applied to the previous tree and would
    // overwrite (revert) the just-made local change once setStructure is called.
    const currentStructure = structureRef.current;
    if (!currentStructure || !currentStructure.Structure) {
      console.warn('[SignalR SIPA] Structure not loaded yet, update will be missed. Consider fetching again.');
      return;
    }

    console.log('[SignalR SIPA] Processing', answers.length, 'answer updates');

    // Build the set of locally-dirty controls (unsynced edits) and any controls
    // that are currently queued in pendingDeltasRef. We must NOT clobber these
    // with incoming SignalR values — doing so causes the user's in-progress
    // edits to disappear when another user saves elsewhere on the report.
    let dirtyLocal;
    try {
      const raw = localStorage.getItem(LS_KEY_DIRTY(reportId));
      dirtyLocal = raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { dirtyLocal = new Set(); }
    const pendingLocal = pendingDeltasRef.current;

    const patched = sanitizeStructureTree({
      ...currentStructure,
      Structure: (currentStructure.Structure || []).map(cat => ({
        ...cat,
        Sections: (cat.Sections || []).map(sec => ({
          ...sec,
          SubSections: (sec.SubSections || []).map(sub => ({
            ...sub,
            Controls: (sub.Controls || []).map(ctrl => {
              const incomingAnswer = answers.find(a => a.controlId === ctrl.Id || a.ControlId === ctrl.Id);
              if (incomingAnswer) {
                // Skip if the user has a locally-unsynced or in-flight change
                // for this control — local intent wins until it is acknowledged.
                if (dirtyLocal.has(ctrl.Id) || pendingLocal.has(ctrl.Id)) {
                  console.log('[SignalR SIPA] Skipping control', ctrl.Id, '— local edit is dirty');
                  return ctrl;
                }
                const newValue = incomingAnswer.value || incomingAnswer.Value;
                // Capture server timestamp if provided, so the next save can
                // pass the correct concurrency token.
                const incomingTs = incomingAnswer.updatedUtc || incomingAnswer.UpdatedUtc;
                if (incomingTs) serverTimestampsRef.current.set(ctrl.Id, incomingTs);
                console.log('[SignalR SIPA] Updating control:', ctrl.Id, 'with value:', newValue);
                return { ...ctrl, value: newValue };
              }
              return ctrl;
            })
          }))
        }))
      }))
    });
    
    console.log('[SignalR SIPA] Structure updated');
    // Route through intent overlay so any in-flight local edits cannot be
    // visually reverted by an incoming SignalR broadcast.
    const finalPatched = applyIntentOverlay(patched);
    structureRef.current = finalPatched;
    setStructure(finalPatched);

    // Show a visual notification
    const userName = update.ChangedByUserName || update.changedByUserName || 'another user';
    setStatus(`Updated by ${userName}`);
    setTimeout(() => setStatus('Ready'), 3000);
    // Intentionally NOT depending on `structure` — we read structureRef.current
    // so this callback identity stays stable and we never use a stale snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, user]);

  const handleReportSignedOff = useCallback((update) => {
    console.log('[SignalR SIPA] Report signed off:', update);
    
    // Update report meta to reflect sign-off
    setReportMeta(prev => ({
      ...(prev || {}),
      finalSigned: true,
      finalSignedByRole: update.Role,
      finalSignedDate: update.Timestamp
    }));
    
    // Show notification
    setStatus(`Signed off by ${update.SignedByUserName || 'another user'} (${update.Role})`);
  }, []);

  const handleUserJoined = useCallback(() => {
    console.log('[SignalR SIPA] User joined (name removed to prevent cleartext logging)');
    // Note: activeUsers state is managed by useSipaSignalR hook itself via UserJoinedReport event
  }, []);

  const handleUserLeft = useCallback(() => {
    console.log('[SignalR SIPA] User left (name removed to prevent cleartext logging)');
    // Note: activeUsers state is managed by useSipaSignalR hook itself via UserLeftReport event
  }, []);

  const handleFieldEditStarted = useCallback((update) => {
    const controlId = update.ControlId || update.controlId;
    console.log('[SignalR SIPA] Field edit started on control:', controlId, '(user name removed to prevent cleartext logging)');
  }, []);

  const handleFieldEditEnded = useCallback((update) => {
    const controlId = update.ControlId || update.controlId;
    console.log('[SignalR SIPA] Field edit ended:', controlId);
  }, []);

  // Initialize SignalR hook for real-time collaboration
  const {
    isConnected: signalRConnected,
    activeUsers,
    // Commented out unused variables - keeping for potential future use
    // fieldEditingStatus,
    // notifyFieldEditStart,
    // notifyFieldEditEnd
  } = useSipaSignalR({
    reportId: reportId,
    onAnswersUpdated: handleAnswersUpdated,
    onReportSignedOff: handleReportSignedOff,
    onUserJoined: handleUserJoined,
    onUserLeft: handleUserLeft,
    onFieldEditStarted: handleFieldEditStarted,
    onFieldEditEnded: handleFieldEditEnded,
    enabled: !!reportId && !editingDisabled, // Only enable if we have a reportId and editing is allowed
  });


  // ------------------------------ Local Queue Helpers ------------------------------
  const getQueue = () => { try { return JSON.parse(localStorage.getItem(LS_KEY_QUEUE) || '[]'); } catch (catchError) { Sentry.captureException(catchError); return []; } };
  const setQueueLS = (q) => { localStorage.setItem(LS_KEY_QUEUE, JSON.stringify(q)); };
  const loadEnforcedSubSections = () => { try { const stored = localStorage.getItem(LS_KEY_ENFORCED(reportId)); if (stored) setEnforcedSubSections(new Set(JSON.parse(stored))); } catch (catchError) { Sentry.captureException(catchError); } };
  const persistEnforcedSubSections = (subIdsSet) => { try { localStorage.setItem(LS_KEY_ENFORCED(reportId), JSON.stringify(Array.from(subIdsSet))); } catch (catchError) { Sentry.captureException(catchError); } };

  // Dirty-control tracking: controls that have local changes not yet confirmed by the server
  const getDirtySet = () => { try { const raw = localStorage.getItem(LS_KEY_DIRTY(reportId)); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch (catchError) { Sentry.captureException(catchError); return new Set(); } };
  const persistDirtySet = (s) => { try { localStorage.setItem(LS_KEY_DIRTY(reportId), JSON.stringify([...s])); } catch (catchError) { Sentry.captureException(catchError); } };
  const clearDirtySet = () => { try { localStorage.removeItem(LS_KEY_DIRTY(reportId)); } catch (catchError) { Sentry.captureException(catchError); } };

  // ------------------------------ Delta-Based Persistence ------------------------------
  // Ref accumulates changed controls during the debounce window: Map<controlId, value>
  const pendingDeltasRef = useRef(new Map());
  // Ref tracks the last-known server timestamp per control for concurrency guard
  const serverTimestampsRef = useRef(new Map());
  // Ref mirror of the latest structure state. Callbacks that produce a new
  // structure from the current one MUST read from this ref — not from the
  // `structure` closure variable — otherwise rapid edits or SignalR updates
  // arriving inside the same render cycle will be computed off a stale tree
  // and silently revert the user's most recent answer.
  const structureRef = useRef(null);
  useEffect(() => { structureRef.current = structure; }, [structure]);

  // ------------------------------ Intent Overlay ------------------------------
  // Authoritative record of the user's MOST RECENT intent for each control.
  // This is the last line of defence against "value reverts" caused by any
  // race condition (SignalR, stale-reconcile, auto-modal re-renders, etc.).
  //
  // Rules:
  //   • Every local edit writes its value here BEFORE setStructure runs.
  //   • Every structure mutation passes through applyIntentOverlay so the
  //     user's intent is re-applied on top of whatever the new tree contains.
  //   • An entry is removed ONLY when the server confirms that exact value
  //     AND no newer pending delta exists for the control. This guarantees
  //     the screen never shows a value the user did not type, even if some
  //     other code path tries to write back an older value.
  const intentRef = useRef(new Map());

  const applyIntentOverlay = useCallback((struct) => {
    if (!struct || !struct.Structure || intentRef.current.size === 0) return struct;
    const intent = intentRef.current;
    let changed = false;
    const next = {
      ...struct,
      Structure: (struct.Structure || []).map(cat => ({
        ...cat,
        Sections: (cat.Sections || []).map(sec => ({
          ...sec,
          SubSections: (sec.SubSections || []).map(sub => ({
            ...sub,
            Controls: (sub.Controls || []).map(ctrl => {
              if (intent.has(ctrl.Id)) {
                const intended = intent.get(ctrl.Id);
                if (ctrl.value !== intended) {
                  changed = true;
                  return { ...ctrl, value: intended };
                }
              }
              return ctrl;
            })
          }))
        }))
      }))
    };
    return changed ? next : struct;
  }, []);

  // setStructure wrapper that ALWAYS applies the intent overlay. Use this in
  // place of setStructure anywhere a mutation could otherwise drop a user
  // edit (SignalR receive, stale reconcile, structure refetch, etc.).
  const setStructureWithIntent = useCallback((updater) => {
    setStructure(prev => {
      const candidate = typeof updater === 'function' ? updater(prev) : updater;
      const final = applyIntentOverlay(candidate);
      structureRef.current = final;
      return final;
    });
  }, [applyIntentOverlay]);

  const persistAnswersDebounced = useDebounce((answersMap) => { 
    localStorage.setItem(LS_KEY_ANSWERS(reportId), JSON.stringify(answersMap)); 
    setAutoSaveTS(new Date()); 
  }, 400);

  // Enqueue a control change into the pending delta map and mark it dirty
  const enqueueChange = useCallback((controlId, value) => {
    pendingDeltasRef.current.set(controlId, value);
    // Record intent so any later structure mutation cannot revert this value.
    intentRef.current.set(controlId, value);
    // Mark control as dirty (unsynced) in localStorage
    const dirty = getDirtySet();
    dirty.add(controlId);
    persistDirtySet(dirty);
  }, [reportId]);

  // Enqueue multiple control changes at once (e.g., section NA toggle)
  const enqueueChanges = useCallback((changes) => {
    const dirty = getDirtySet();
    changes.forEach(({ controlId, value }) => {
      pendingDeltasRef.current.set(controlId, value);
      intentRef.current.set(controlId, value);
      dirty.add(controlId);
    });
    persistDirtySet(dirty);
  }, [reportId]);

  // Merge deltas into the per-report queue entry in localStorage.
  // Stores the same query-string flags used by online saves so deferred/offline sync
  // replays with equivalent server-side behaviour once connectivity is restored.
  const mergeIntoQueue = useCallback((deltas) => {
    if (!deltas.length) return;

    const queuedQueryString = new URLSearchParams();
    queuedQueryString.set('reScore', String(reScore));
    if (applyEnf) {
      queuedQueryString.set('applyEnforcements', 'true');
    }

    const queue = getQueue();
    const existing = queue.find(item => String(item.reportId) === String(reportId));
    if (existing) {
      // Merge: newer delta values overwrite older ones for the same controlId.
      // Also refresh the stored query-string flags so the queued request reflects
      // the latest user-selected sync behaviour for this report.
      const merged = new Map((existing.payload?.answers || []).map(a => [a.controlId, a.value]));
      deltas.forEach(d => merged.set(d.controlId, d.value));
      existing.payload = { answers: [...merged.entries()].map(([controlId, value]) => ({ controlId, value })) };
      existing.qs = queuedQueryString.toString();
      existing.ts = Date.now();
    } else {
      queue.push({ reportId, payload: { answers: deltas }, qs: queuedQueryString.toString(), ts: Date.now() });
    }
    setQueueLS(queue);
    setPendingSync(true);
  }, [applyEnf, reportId, reScore]);

  // Populate serverTimestampsRef from the structure's AnswerUpdatedUtc fields
  const populateServerTimestamps = (struct) => {
    traverseControls(struct, (ctrl) => {
      const ts = ctrl.AnswerUpdatedUtc || ctrl.answerUpdatedUtc;
      if (ts) serverTimestampsRef.current.set(ctrl.Id, ts);
    });
  };

  // Fetch server total stats
  const fetchTotalStats = useCallback(async () => {
    if(!reportId) return;
    try {
      const res = await apiFetch(`/api/sipa/reports/total/${reportId}`);
      if(res.ok){ const data = await res.json(); setTotalStats(data); }
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* ignore */
    }
  }, [apiFetch, reportId]);

  // Flush pending deltas to the server (or queue if offline). Called via debounce.
  const flushDeltasToServer = useDebounce(() => { 
    if (!reportId) return; 
    const deltas = [...pendingDeltasRef.current.entries()].map(([controlId, value]) => ({ 
      controlId, 
      value,
      // Attach the last-known server timestamp for concurrency guard
      updatedUtc: serverTimestampsRef.current.get(controlId) || null
    }));
    pendingDeltasRef.current.clear();
    if (!deltas.length) return;

    const qs = new URLSearchParams(); 
    if (applyEnf) qs.append('applyEnforcements', 'true'); 
    if (reScore) qs.append('reScore', 'true');
    // Opt in to receive server-authoritative timestamps for accepted answers
    // so the concurrency guard uses accurate values instead of local clock
    qs.append('includeAcceptedMetadata', 'true');
    const requestBody = { answers: deltas };

    if (!navigator.onLine) { 
      mergeIntoQueue(deltas);
      setStatus('Offline (queued)'); 
      return; 
    } 

    apiFetch(`/api/sipa/reports/answers/${reportId}?${qs.toString()}`, { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify(requestBody) 
    })
      .then(async (r) => { 
        if (r.status === 204) {
          // All answers accepted (legacy path — no metadata returned).
          // IMPORTANT: re-read the dirty set immediately before mutating so we
          // don't trample new entries that enqueueChange may have written while
          // the request was in flight. Also, do NOT clear dirty for controls
          // that already have a newer pending delta — the next flush still
          // needs to send those.
          const dirty = getDirtySet();
          const pending = pendingDeltasRef.current;
          deltas.forEach(d => {
            if (!pending.has(d.controlId)) {
              dirty.delete(d.controlId);
              // Only clear intent if the user has not typed a newer value.
              // If intentRef holds a different value than what we just sent,
              // a newer edit happened mid-flight and we must keep intent.
              if (intentRef.current.get(d.controlId) === d.value) {
                intentRef.current.delete(d.controlId);
              }
            }
            serverTimestampsRef.current.set(d.controlId, new Date().toISOString());
          });
          if (dirty.size === 0) clearDirtySet(); else persistDirtySet(dirty);
          setStatus('Autosaved');
        } else if (r.ok) {
          // Server returned JSON with accepted/stale details and authoritative timestamps
          const data = await r.json();
          const dirty = getDirtySet();
          const pending = pendingDeltasRef.current;

          // Update timestamps from server-authoritative acceptedAnswers if available
          const acceptedMap = new Map(
            (data.acceptedAnswers || []).map(a => [a.controlId ?? a.ControlId, a.updatedUtc ?? a.UpdatedUtc])
          );
          const staleControlIds = new Set((data.stale || []).map(s => s.controlId));

          deltas.forEach(d => {
            if (!staleControlIds.has(d.controlId)) {
              // Only clear the dirty flag when there is no newer pending edit
              // queued for this control — otherwise the next flush still has
              // unsynced work for it.
              if (!pending.has(d.controlId)) {
                dirty.delete(d.controlId);
                // Clear intent only if the value we just sent still matches
                // the user's latest intent. A newer edit during the request
                // would have updated intentRef to something else.
                if (intentRef.current.get(d.controlId) === d.value) {
                  intentRef.current.delete(d.controlId);
                }
              }
              // Prefer server-authoritative timestamp; fall back to client clock only as last resort
              const serverTs = acceptedMap.get(d.controlId);
              serverTimestampsRef.current.set(d.controlId, serverTs || new Date().toISOString());
            }
          });
          // Reconcile stale answers: update local structure with server's newer values.
          // But never overwrite a control the user is actively editing right now
          // (it has a pending delta) — their next flush will resolve the conflict.
          if (data.stale && data.stale.length > 0) {
            const staleMap = new Map(
              data.stale
                .filter(s => !pending.has(s.controlId))
                .map(s => [s.controlId, s])
            );
            if (staleMap.size > 0) {
              setStructureWithIntent(prev => {
                if (!prev) return prev;
                const next = sanitizeStructureTree({
                  ...prev,
                  Structure: (prev.Structure || []).map(cat => ({
                    ...cat,
                    Sections: (cat.Sections || []).map(sec => ({
                      ...sec,
                      SubSections: (sec.SubSections || []).map(sub => ({
                        ...sub,
                        Controls: (sub.Controls || []).map(ctrl => {
                          const staleInfo = staleMap.get(ctrl.Id);
                          if (staleInfo) {
                            serverTimestampsRef.current.set(ctrl.Id, staleInfo.serverUpdatedUtc);
                            dirty.delete(ctrl.Id);
                            return { ...ctrl, value: staleInfo.serverValue };
                          }
                          return ctrl;
                        })
                      }))
                    }))
                  }))
                });
                return next;
              });
            }
            if (dirty.size === 0) clearDirtySet(); else persistDirtySet(dirty);
            setStatus(`Synced (${staleMap.size} conflict(s) resolved)`);
          } else {
            if (dirty.size === 0) clearDirtySet(); else persistDirtySet(dirty);
            setStatus('Autosaved');
          }
        } else if (r.status === 403) {
          // Read-only user — revert local answers to server values
          // Re-fetch structure to get authoritative data
          try {
            const freshRes = await apiFetch(`/api/sipa/reports/structure/${reportId}`);
            if (freshRes.ok) {
                const freshData = await freshRes.json();
                const normalizedRoot = freshData.Structure ? sanitizeStructureTree(freshData) : adaptServerStructure(freshData);
                // Read-only 403: server is authoritative — clear intent so we
                // don't keep re-applying rejected local edits.
                intentRef.current.clear();
                setStructure(normalizedRoot);
                populateServerTimestamps(normalizedRoot);
                localStorage.setItem(LS_KEY_STRUCTURE(reportId), JSON.stringify(normalizedRoot));
                clearDirtySet();
              }
          }
          catch (catchError) {
              Sentry.captureException(catchError);
              /* fallback: revert deltas from structure */
          }
          // Clear queue for this report
          const q = getQueue();
          setQueueLS(q.filter(item => String(item.reportId) !== String(reportId)));
          setStatus('Read-only — changes reverted');
        } else {
          throw new Error(`Save failed with status: ${r.status}`);
        }
        // Remove this report from queue if it was queued
        const queue = getQueue();
        const filtered = queue.filter(item => String(item.reportId) !== String(reportId));
        if (filtered.length !== queue.length) {
          setQueueLS(filtered);
          setPendingSync(filtered.length > 0);
        }
        return fetchTotalStats();
      })
      .catch((error) => { 
        // Queue for retry on error
        mergeIntoQueue(deltas);
        setStatus(`Queued (${error.message || 'error'})`); 
      }); 
  }, 1500);

  // ------------------------------ Structure Load ------------------------------
  // Restore only DIRTY (unsynced) local answers on top of the server structure.
  // Server data is authoritative for controls that have been successfully synced.
  const restoreLocalAnswers = (baseStruct) => {
    try {
      const dirty = getDirtySet();
      const queueHasPending = getQueue().some(item => String(item.reportId) === String(reportId));
      // If nothing is dirty and nothing is queued, server data is fully authoritative
      if (dirty.size === 0 && !queueHasPending) {
        localStorage.removeItem(LS_KEY_ANSWERS(reportId));
        return;
      }
      const raw = localStorage.getItem(LS_KEY_ANSWERS(reportId));
      if (!raw) return;
      const map = JSON.parse(raw);
      // Only overlay controls that are in the dirty set (not yet confirmed by server)
      const patched = sanitizeStructureTree({
        ...baseStruct,
        Structure: (baseStruct.Structure || []).map(cat => ({
          ...cat,
          Sections: (cat.Sections || []).map(sec => ({
            ...sec,
            SubSections: (sec.SubSections || []).map(sub => ({
              ...sub,
              Controls: (sub.Controls || []).map(ctrl =>
                dirty.has(ctrl.Id) && map[ctrl.Id] !== undefined ? { ...ctrl, value: map[ctrl.Id] } : ctrl
              )
            }))
          }))
        }))
      });
      setStructure(applyIntentOverlay(patched));
      // Recompute enforcement after restoring answers so stale flags are removed.
      const recomputed = recomputeEnforcedSubSections(patched);
      setEnforcedSubSections(recomputed);
      persistEnforcedSubSections(recomputed);
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* empty */
    }
  };
  const deriveMetaFromStructure = (data) => { if (!data) return null; const meta = {}; ['PropertyName','propertyName','SiteName','siteName','periodLabel','PeriodLabel','periodStart','PeriodStart','centreManagerSigned','CentreManagerSigned','finalSigned','FinalSigned'].forEach(k => { if (data[k] !== undefined) meta[k] = data[k]; }); return meta; };

  const loadStructure = useCallback(async () => { setStatus('Loading...'); if (!reportId) { navigate('/sipa/reports', { replace: true }); return; } const local = localStorage.getItem(LS_KEY_STRUCTURE(reportId)); if (local) { try { const obj = sanitizeStructureTree(JSON.parse(local)); setStructure(applyIntentOverlay(obj)); setReportMeta(prev => prev || deriveMetaFromStructure(obj)); setStatus(navigator.onLine ? 'Cached (syncing...)' : 'Offline (cached)');
        const recomputedCached = recomputeEnforcedSubSections(obj); setEnforcedSubSections(recomputedCached); persistEnforcedSubSections(recomputedCached); populateServerTimestamps(obj); } catch (catchError) { Sentry.captureException(catchError); } } if (!navigator.onLine) { fetchTotalStats(); return; } try { const res = await apiFetch(`/api/sipa/reports/structure/${reportId}`); if (res.ok) { const data = await res.json(); const normalizedRoot = data.Structure ? sanitizeStructureTree(data) : adaptServerStructure(data); setStructure(applyIntentOverlay(normalizedRoot)); setReportMeta(deriveMetaFromStructure(normalizedRoot)); localStorage.setItem(LS_KEY_STRUCTURE(reportId), JSON.stringify(normalizedRoot)); setStatus('Ready'); populateServerTimestamps(normalizedRoot); restoreLocalAnswers(normalizedRoot); // If no local answers, still recompute enforcement from server payload
        if (!localStorage.getItem(LS_KEY_ANSWERS(reportId))) { const recomputedServer = recomputeEnforcedSubSections(normalizedRoot); setEnforcedSubSections(recomputedServer); persistEnforcedSubSections(recomputedServer); }
        fetchTotalStats(); } else if (res.status === 404) setStatus('Not found'); else setStatus('Load failed'); } catch (catchError) { Sentry.captureException(catchError); setStatus('Load error'); } }, [apiFetch, navigate, reportId]);

  // Fetch action item counts — used by SignalR event handlers and manual refreshes
  const fetchActionItemCounts = useCallback(async () => { if (!structure?.PropertyId && !structure?.propertyId) return; try { const propertyId = structure.PropertyId || structure.propertyId; const res = await apiFetch(`/api/SipaCompletion/sipa/action-item-counts?propertyId=${propertyId}`); const data = await res.json().catch(() => null); if (res.ok && data?.success && data.counts) setActionCounts(data.counts); } catch (catchError) { Sentry.captureException(catchError); } }, [apiFetch, structure]);
  // Inline the fetch with a .then() chain so setActionCounts is called inside a Promise
  // callback rather than synchronously in the effect body (react-hooks/set-state-in-effect)
  useEffect(() => {
    const pid = structure?.PropertyId || structure?.propertyId;
    if (!pid) return;
    let active = true;
    apiFetch(`/api/SipaCompletion/sipa/action-item-counts?propertyId=${pid}`)
      .then(res => res.ok ? res.json().catch(() => null) : null)
      .then(data => { if (active && data?.success && data.counts) setActionCounts(data.counts); })
      .catch(e => Sentry.captureException(e));
    return () => { active = false; };
  }, [structure?.PropertyId, structure?.propertyId, apiFetch]);

  // Listen for action item creation events via SignalR to refresh counts in real-time
  const propertyId = structure?.PropertyId || structure?.propertyId;
  useActionItemSignalR({
    onActionItemUpdated: useCallback((update) => {
      // Check if this is a SIPA action item for this property
      const currentPropertyId = structure?.PropertyId || structure?.propertyId;
      const updatePropertyId = update?.PropertyID || update?.PropertyId || update?.propertyId;
      const updateType = update?.Type || update?.type;
      const actionItemsType = update?.ActionItemsType || update?.actionItemsType;
      
      console.log('[SignalR SIPA] Action item update received:', {
        type: updateType,
        actionItemsType,
        updatePropertyId,
        currentPropertyId,
        fullUpdate: update
      });
      
      // Handle both creation and status changes for SIPA action items on this property
      if (actionItemsType === 'SIPA' && updatePropertyId == currentPropertyId) {
        const subSectionId = update?.RelatedId || update?.relatedId || update?.SubSectionId || update?.subSectionId;
        
        if (updateType === 'Created') {
          console.log('[SignalR SIPA] SIPA action item created for this property, updating counts...');
          console.log('[SignalR SIPA] SubSection ID from update:', subSectionId, 'Type:', typeof subSectionId);
          
          if (subSectionId) {
            // Convert to number for consistent comparison (action counts keys are numbers)
            const subSectionIdNum = parseInt(subSectionId, 10);
            
            // Update the count for this specific subsection immediately
            setActionCounts(prev => {
              console.log('[SignalR SIPA] Current actionCounts state:', prev);
              const currentCount = prev[subSectionIdNum] || 0;
              const newCount = currentCount + 1;
              console.log(`[SignalR SIPA] Updating count for subsection ${subSectionIdNum}: ${currentCount} → ${newCount}`);
              return {
                ...prev,
                [subSectionIdNum]: newCount
              };
            });
          } else {
            console.warn('[SignalR SIPA] No SubSectionId found in update:', update);
          }
        } else if (updateType === 'StatusChange') {
          // Status changed - refresh counts from server since we need to check if status is now active/inactive
          console.log('[SignalR SIPA] SIPA action item status changed, refreshing counts from server...');
          fetchActionItemCounts();
          return; // Don't do the delayed refresh below
        }
        
        // Also refresh full counts from server to ensure consistency (after short delay)
        setTimeout(() => {
          console.log('[SignalR SIPA] Refreshing all counts from server...');
          fetchActionItemCounts();
        }, 500);
      }
    }, [structure, fetchActionItemCounts]),
    enabled: !!propertyId,
  });

  // Stable refs so effects can call these functions without the linter tracing
  // setState calls through the useCallback (react-hooks/set-state-in-effect)
  const loadStructureRef = useRef(loadStructure);
  useEffect(() => { loadStructureRef.current = loadStructure; });
  const loadEnforcedRef = useRef(loadEnforcedSubSections);
  useEffect(() => { loadEnforcedRef.current = loadEnforcedSubSections; });

  // Load structure when reportId changes
  useEffect(() => {
    if (reportId) {
      loadStructureRef.current();
      loadEnforcedRef.current();
    }
  }, [reportId]);

  // Fetch SIPA workflow permissions for the current user
  useEffect(() => {
    const fetchWorkflowPerms = async () => {
      try {
        const res = await apiFetch('/api/sipa/workflow-config/user-permissions');
        const data = await res.json().catch(() => null);
        if (res.ok && data?.success && data.data) {
          setWorkflowPerms(data.data);
        }
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* empty */
      }
    };
    fetchWorkflowPerms();
  }, [apiFetch]);

  // NEW: Online/offline detection and queue processing with periodic retry
  const retryTimerRef = useRef(null);
  const processQueueRef = useRef(null); // stable ref to avoid stale closures

  // Process queued save requests when coming back online
  const processQueue = useCallback(async () => {
    const queue = getQueue();
    if (!queue.length) {
      setPendingSync(false);
      return;
    }

    const failed = [];
    for (const item of queue) {
      try {
        let payload = item.payload;
        if (Array.isArray(payload)) {
          payload = { answers: payload };
        } else if (payload && !payload.answers) {
          payload = { answers: [] };
        }

        // Attach timestamps from serverTimestampsRef for concurrency guard
        if (payload.answers) {
          payload = {
            answers: payload.answers.map(a => ({
              ...a,
              updatedUtc: a.updatedUtc || serverTimestampsRef.current.get(a.controlId) || null
            }))
          };
        }

        const res = await apiFetch(
          `/api/sipa/reports/answers/${item.reportId}?${item.qs || ''}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );

        if (res.status === 204) {
          // All accepted — but preserve dirty for controls with newer pending edits
          const dirty = getDirtySet();
          const pending = pendingDeltasRef.current;
          (payload.answers || []).forEach(a => {
            if (!pending.has(a.controlId)) dirty.delete(a.controlId);
            serverTimestampsRef.current.set(a.controlId, new Date().toISOString());
          });
          if (dirty.size === 0) clearDirtySet(); else persistDirtySet(dirty);
        } else if (res.ok) {
          // Partial accept — reconcile stale answers
          const data = await res.json();
          const dirty = getDirtySet();
          const pending = pendingDeltasRef.current;
          const staleControlIds = new Set((data.stale || []).map(s => s.controlId));
          (payload.answers || []).forEach(a => {
            if (!staleControlIds.has(a.controlId) && !pending.has(a.controlId)) {
              dirty.delete(a.controlId);
              serverTimestampsRef.current.set(a.controlId, new Date().toISOString());
            }
          });
          // Reconcile stale answers into local structure (skip controls with pending newer edits)
          if (data.stale && data.stale.length > 0) {
            const staleMap = new Map(
              data.stale
                .filter(s => !pending.has(s.controlId))
                .map(s => [s.controlId, s])
            );
            if (staleMap.size > 0) {
              setStructureWithIntent(prev => {
                if (!prev) return prev;
                const next = sanitizeStructureTree({
                  ...prev,
                  Structure: (prev.Structure || []).map(cat => ({
                    ...cat,
                    Sections: (cat.Sections || []).map(sec => ({
                      ...sec,
                      SubSections: (sec.SubSections || []).map(sub => ({
                        ...sub,
                        Controls: (sub.Controls || []).map(ctrl => {
                          const staleInfo = staleMap.get(ctrl.Id);
                          if (staleInfo) {
                            serverTimestampsRef.current.set(ctrl.Id, staleInfo.serverUpdatedUtc);
                            dirty.delete(ctrl.Id);
                            return { ...ctrl, value: staleInfo.serverValue };
                          }
                          return ctrl;
                        })
                      }))
                    }))
                  }))
                });
                return next;
              });
            }
          }
          if (dirty.size === 0) clearDirtySet(); else persistDirtySet(dirty);
        } else {
          failed.push(item);
        }
      } catch (catchError) {
          Sentry.captureException(catchError);
        failed.push(item);
      }
    }

    if (failed.length === 0) {
      setQueueLS([]);
      setPendingSync(false);
      setStatus('All changes synced');
      fetchTotalStats();
    } else {
      setQueueLS(failed);
      setPendingSync(true);
      setStatus(`${failed.length} change(s) pending`);
    }
  }, [apiFetch, fetchTotalStats, reportId]);

  // Keep a stable ref so event handlers and timers always call the latest version
  useEffect(() => { processQueueRef.current = processQueue; }, [processQueue]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setStatus('Back online - syncing...');
      processQueueRef.current?.();
      // Start periodic retry: attempt every 30s while items remain queued
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = setInterval(() => {
        const q = getQueue();
        if (q.length > 0) processQueueRef.current?.();
        else clearInterval(retryTimerRef.current);
      }, 30000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setStatus('Offline - changes will be queued');
      clearInterval(retryTimerRef.current);
    };

    // Spill any pending (debounced) deltas into the persistent queue so they
    // survive a page navigation, browser close, or tab hide. Without this, a
    // user can lose the most-recent change(s) if they leave the page during
    // the debounce window — which has been reported as checkboxes / answers
    // "not persisting" after returning to the report.
    const spillPendingDeltas = () => {
      const pending = pendingDeltasRef.current;
      if (!pending || pending.size === 0) return;
      const deltas = [...pending.entries()].map(([controlId, value]) => ({
        controlId,
        value,
        updatedUtc: serverTimestampsRef.current.get(controlId) || null
      }));
      pendingDeltasRef.current.clear();
      mergeIntoQueue(deltas);
    };

    // Warn user before leaving if there are unsaved changes in the queue or dirty controls
    const handleBeforeUnload = (e) => {
      spillPendingDeltas();
      const queue = getQueue();
      const dirty = getDirtySet();
      if (queue.length > 0 || dirty.size > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes that are queued for sync. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    // Mobile browsers / tab switches may not fire beforeunload reliably, so
    // also spill pending deltas when the document becomes hidden.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        spillPendingDeltas();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial queue check on mount (pendingSync already initialised from localStorage above)
    const queue = getQueue();
    if (queue.length > 0) {
      if (navigator.onLine) {
        processQueueRef.current?.();
        retryTimerRef.current = setInterval(() => {
          const q = getQueue();
          if (q.length > 0) processQueueRef.current?.();
          else clearInterval(retryTimerRef.current);
        }, 30000);
      }
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Final safety net: if the component unmounts (e.g., user navigates to
      // another page in the SPA), make sure any queued deltas are persisted.
      spillPendingDeltas();
      clearInterval(retryTimerRef.current);
    };
  }, []);

  // Users (lazy load when opening sign-off)
  const ensureUsersLoaded = async () => { 
    if (usersLoaded) return; 
    try { 
      const res = await apiFetch('/api/User/all'); 
      if (res.ok) { 
        const data = await res.json().catch(()=>null); 
        // Set users to the array at data.data
        setUsers(data?.data || []);
      } 
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* empty */
    }
    finally { setUsersLoaded(true); } 
  };

  // Fetch assigned template (module Operations, context SIPA)
  const fetchAssignedTemplate = async () => {
    try {
      // Get template assignment from ModuleTemplateAssignments API
      const res = await apiFetch('/api/ModuleTemplateAssignments/Operations/SIPA');
      const data = await res.json();
      const key = data?.data?.assignedTemplateKey || data?.data?.AssignedTemplateKey;
      if (!key) return null;
      // Fetch the actual template using the key
      const tplRes = await apiFetch(`/api/Templates/${encodeURIComponent(key)}`);
      const tplData = await tplRes.json();
      if (tplRes.ok && tplData?.success) return tplData.data;
      return null;
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        return null;
    }
  };

  // Action Item list
  const openAiListModal = async (sub) => { setAiListTarget(sub); setAiListModalOpen(true); setAiListLoading(true); try { const propertyId = structure?.PropertyId || structure?.propertyId; const res = await apiFetch(`/api/SipaCompletion/sipa/action-items-by-subsection?propertyId=${propertyId}&subSectionId=${sub.subId}`); const data = await res.json().catch(() => null); if (res.ok && data?.success && Array.isArray(data.items)) setAiListItems(data.items); else setAiListItems([]); } catch (catchError) { Sentry.captureException(catchError); setAiListItems([]); } finally { setAiListLoading(false); } };
  const closeAiListModal = () => { setAiListModalOpen(false); setAiListTarget(null); setAiListItems([]); };

  // Move openAiModal above updateLocalAnswer to avoid temporal dead zone
  const openAiModal = useCallback((sub) => {
    setAiTarget(sub);
    ensureUsersLoaded();
    const currentUserId = user?.userID || user?.id || user?.Id || user?.userId || user?.UserId || null;
    const currentUserName = user?.name || user?.fullName || user?.displayName || user?.email || 'You';
    const baseLabel = sub.subTitle || sub.sectionTitle || '';
    const autoTitle = sub.controlLabel
      ? `${baseLabel}: ${sub.controlLabel}${sub.selectedOptionText ? ' = ' + sub.selectedOptionText : (sub.selectedValue ? ' = ' + sub.selectedValue : '')}` 
      : `${baseLabel}:`;
    setAiForm({ title: autoTitle, description: '', priority: 'Low', comments: '', assignedToUserId: currentUserId });
    setAiUserSearch(currentUserName);
    setShowUserSuggestions(false);
    // Set loading state before fetching template
    setTemplateLoading(true);
    // Fetch module-assigned template for Operations/SIPA before showing modal
    fetchAssignedTemplate()
      .then(tpl => {
        if (tpl) {
          setContextTemplate(tpl);
          setTemplateFormData({});
          setTemplateModalOpen(true);
        } else {
          setAiModalOpen(true);
        }
      })
      .catch(() => {
        setAiModalOpen(true);
      })
      .finally(() => {
        setTemplateLoading(false);
      });
  }, [user]);

  // Handle submit from template modal
  const saveTemplateActionItem = async (formData) => {
    if (!aiTarget || !contextTemplate) { setTemplateModalOpen(false); return; }
    if (!structure?.PropertyId && structure?.propertyId === undefined) { setTemplateModalOpen(false); return; }
    setAiSaving(true);
    try {
      const propertyId = structure.PropertyId || structure.propertyId;
      const payload = {
        propertyId,
        subSectionId: aiTarget.subId,
        title: aiForm.title,
        description: aiForm.description,
        sectionTitle: aiTarget.sectionTitle,
        subTitle: aiTarget.subTitle,
        priority: aiForm.priority,
        comments: aiForm.comments,
        userId: user?.userID || user?.id || user?.Id || null,
        assignedToUserId: aiForm.assignedToUserId,
        templateKey: contextTemplate.templateKey,
        dataJson: JSON.stringify(formData || templateFormData),
        sipaReportId: reportId ? parseInt(reportId, 10) : null
      };
      const res = await apiFetch('/api/SipaCompletion/sipa/action-item', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(payload) 
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        await fetchActionItemCounts();
        setTemplateModalOpen(false);
        setContextTemplate(null);
        if (aiListModalOpen && aiListTarget?.subId === aiTarget.subId) openAiListModal(aiListTarget);
      }
    } catch (catchError) { Sentry.captureException(catchError); } finally {
      setAiSaving(false);
    }
  };

  // Template modal renderer
  const TemplateModal = useMemo(() => {
    if (!templateModalOpen || !contextTemplate) return null;
    return (
      <div className="modal-overlay" style={{ position: 'fixed', inset:0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex:1250, padding: '16px' }} onMouseDown={e => { if (e.target === e.currentTarget) setTemplateModalOpen(false); }}>
        <div className="modal-content" style={{ background: '#fff', borderRadius: '6px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0px 4px 20px rgba(0,0,0,0.15)' }}>
          <div className="modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid #ddd', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ margin:0, fontSize:'1.05rem' }}>Create Action Item (SIPA)</h3>
            <button className="btn-close" onClick={()=>setTemplateModalOpen(false)} style={{ background:'transparent', border:'none', fontSize:'1.2rem', cursor:'pointer', minWidth: 'auto', height: 'auto' }}>✕</button>
          </div>
          <div className="modal-body" style={{ padding: '16px 20px' }}>
            <TemplateRenderer
              template={contextTemplate}
              onSubmit={saveTemplateActionItem}
              onCancel={() => setTemplateModalOpen(false)}
              submitButtonText={aiSaving ? 'Creating...' : 'Create Action Item'}
              readOnly={aiSaving}
            />
          </div>
        </div>
      </div>
    );
  }, [templateModalOpen, contextTemplate, aiSaving, saveTemplateActionItem]);

  // ------------------------------ Derived Collections ------------------------------
  // Build a flattened list of sections and ensure a stable sort by Order
  const sectionRows = useMemo(() => {
    if (!structure) return [];
    const out = [];
    (structure.Structure || []).forEach(cat => {
      (cat.Sections || []).forEach(sec => out.push(sec));
    });
    out.sort((a, b) => (a.Order ?? a.order ?? 0) - (b.Order ?? b.order ?? 0));
    return out;
  }, [structure]);

  const overallScoreInfo = useMemo(() => { if (!sectionRows.length) return { score: null, excludedWeight:0, effectiveWeight:0 }; let numerator =0, includedWeight =0, excludedWeight =0; sectionRows.forEach(sec => { const weight = sec.Weight ||0; if (isSectionFullyNA(sec)) { excludedWeight += weight; return; } const stats = calcSectionStats(sec); if (stats.avg) numerator += parseFloat(stats.avg) * weight; includedWeight += weight; }); if (includedWeight ===0) return { score: null, excludedWeight, effectiveWeight:0 }; return { score: (numerator / includedWeight).toFixed(2), excludedWeight, effectiveWeight: includedWeight }; }, [sectionRows]);
  const outstandingEnforced = useMemo(() => { const list = []; enforcedSubSections.forEach(id => { if ((actionCounts[id] ||0) ===0) list.push(id); }); return list; }, [enforcedSubSections, actionCounts]);

  // NEW: Category scores (weighted like overall; excludes fully NA sections)
  const categoryScores = useMemo(() => {
    if (!structure) return [];
    return (structure.Structure || []).map(cat => {
      let numerator =0, includedWeight =0;
      (cat.Sections || []).forEach(sec => {
        const weight = sec.Weight ||0;
        if (isSectionFullyNA(sec)) return;
        const stats = calcSectionStats(sec);
        if (stats.avg) {
          numerator += parseFloat(stats.avg) * weight;
          includedWeight += weight;
        }
      });
      const score = includedWeight ===0 ? null : (numerator / includedWeight);
      // Added fallback for server 'Category' property (was previously omitted, causing generic label)
      const name = cat.CategoryName || cat.Category || cat.category || 'Category';
      return { name, score, includedWeight };
    });
  }, [structure]);

  // NEW: identify subsections with scoring controls missing an answer.
  // A scoring control is any control not marked ExcludeFromScore.
  // Answer considered present if value is not undefined/null/'' ("false" counts for checkbox, "NA" counts if chosen).
  const incompleteScoringSubSections = useMemo(() => {
    if (!structure) return [];
    const missing = new Set();
    (structure.Structure || []).forEach(cat => (cat.Sections || []).forEach(sec => (sec.SubSections || []).forEach(sub => {
      let hasMissing = false;
      (sub.Controls || []).forEach(ctrl => {
        if (hasMissing) return; // already flagged this subsection
        if (ctrl.ExcludeFromScore) return; // ignore non-scoring controls
        const val = ctrl.value;
        const answered = !(val === undefined || val === null || val === '');
        if (!answered) {
          hasMissing = true;
        }
      });
      if (hasMissing) missing.add(sub.Id);
    })));
    return Array.from(missing);
  }, [structure]);

  // Map for subsection display (e.g., "1.5 Customer Vehicle Entrance")
  const subDisplayMap = useMemo(() => {
    const map = {}; let secCounter = 0; (sectionRows || []).forEach(sec => { secCounter++; let subCounter = 0; (sec.SubSections || []).forEach(sub => { subCounter++; map[sub.Id] = `${secCounter}.${subCounter} ${sub.Title}`; }); }); return map;
  }, [sectionRows]);
  // Map subId -> sectionId for expansion before scroll
  const subToSectionIdMap = useMemo(() => {
    const map = {}; (sectionRows || []).forEach(sec => (sec.SubSections || []).forEach(sub => { map[sub.Id] = sec.Id; })); return map;
  }, [sectionRows]);

  // Scroll to a specific subsection when the URL contains a hash like #sub_123.
  // Expands the parent section first so the target element is visible in the DOM.
  const hashScrolledRef = useRef(false);
  const highlightTimerRef = useRef(null);
  useEffect(() => {
    if (hashScrolledRef.current || !sectionRows.length) return;
    const hash = location.hash; // e.g. "#sub_270"
    if (!hash || !hash.startsWith('#sub_')) return;
    const subIdStr = hash.replace('#sub_', '');
    const subId = parseInt(subIdStr, 10);
    if (isNaN(subId)) return;

    const parentSectionId = subToSectionIdMap[subId];
    hashScrolledRef.current = true;

    // Combine section expansion and scroll inside a single setTimeout so that
    // setCollapsed / setHighlightSubs are called asynchronously — satisfies
    // react-hooks/set-state-in-effect. Double-RAF after expansion gives React
    // one render cycle to show the expanded section before scrolling.
    const scrollTimerId = setTimeout(() => {
      if (parentSectionId && collapsed[parentSectionId]) {
        setCollapsed(prev => ({ ...prev, [parentSectionId]: false }));
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById(`sub_${subId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightSubs(new Set([subId]));
            highlightTimerRef.current = setTimeout(() => setHighlightSubs(new Set()), 3000);
          }
        });
      });
    }, 500);

    return () => {
      clearTimeout(scrollTimerId);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [sectionRows, location.hash, subToSectionIdMap, collapsed]);

  // ------------------------------ Section NA Helpers ------------------------------
  // Returns true when every scoring control in the section supports NA (either AllowNotApplicable or has an option Value === 'NA')
  const sectionSupportsNA = useCallback((sec) => {
    if (!sec || !sec.SubSections) return false;
    return (sec.SubSections || []).every(sub => (sub.Controls || []).every(ctrl => {
      if (ctrl.ExcludeFromScore) return true; // ignore non-scoring controls
      if (ctrl.AllowNotApplicable) return true;
      return (ctrl.Options || []).some(o => String(o.Value || '').toUpperCase() === 'NA');
    }));
  }, []);

  // Returns true when all scoring controls in the section currently have value 'NA'
  const sectionIsAllNA = useCallback((sec) => {
    if (!sec || !sec.SubSections) return false;
    return (sec.SubSections || []).every(sub => (sub.Controls || []).every(ctrl => {
      if (ctrl.ExcludeFromScore) return true;
      return ctrl.value === 'NA';
    }));
  }, []);

  // Toggle all scoring controls in a section to 'NA' (or clear them) — applies only to controls that support NA
  const setSectionNotApplicable = useCallback((sectionId, toNA) => {
    const currentStructure = structureRef.current;
    if (!currentStructure) return;
    const patched = sanitizeStructureTree({
      ...currentStructure,
      Structure: (currentStructure.Structure || []).map(cat => ({
        ...cat,
        Sections: (cat.Sections || []).map(sec => {
          if (sec.Id !== sectionId) return sec;
          return {
            ...sec,
            SubSections: (sec.SubSections || []).map(sub => ({
              ...sub,
              Controls: (sub.Controls || []).map(ctrl => {
                if (ctrl.ExcludeFromScore) return ctrl;
                const supportsNA = ctrl.AllowNotApplicable || (ctrl.Options || []).some(o => String(o.Value || '').toUpperCase() === 'NA');
                if (!supportsNA) return ctrl;
                return { ...ctrl, value: toNA ? 'NA' : '' };
              })
            }))
          };
        })
      }))
    });

    structureRef.current = patched;
    setStructure(patched);

    // Persist answers once for the whole structure change
    const answersMap = {};
    traverseControls(patched, ctrl => {
      if (ctrl.value !== undefined && ctrl.value !== null && ctrl.value !== '') {
        answersMap[ctrl.Id] = ctrl.value;
      }
    });
    persistAnswersDebounced(answersMap);

    // Collect changed controls as deltas and flush to server.
    // Build a lookup of the original control values once so we avoid
    // repeatedly traversing the full original structure for every patched control.
    // This keeps the comparison logic functionally identical while reducing
    // the overall complexity from O(n²) to O(n).
    const originalControlValueById = new Map();
    traverseControls(currentStructure, (originalControl) => {
      originalControlValueById.set(originalControl.Id, originalControl.value);
    });

    const changedControls = [];
    traverseControls(patched, (ctrl) => {
      const originalValue = originalControlValueById.get(ctrl.Id);
      if (ctrl.value !== originalValue) {
        changedControls.push({ controlId: ctrl.Id, value: ctrl.value ?? null });
      }
    });
    if (changedControls.length) {
      enqueueChanges(changedControls);
      flushDeltasToServer();
    }

    // Recompute enforcement and persist
    const recomputed = recomputeEnforcedSubSections(patched);
    setEnforcedSubSections(recomputed);
    persistEnforcedSubSections(recomputed);

    setStatus(toNA ? 'Section marked Not Applicable' : 'Section NA cleared');
    setTimeout(() => setStatus('Ready'), 2000);
    // structure intentionally read via ref — see structureRef comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistAnswersDebounced, enqueueChanges, flushDeltasToServer, persistEnforcedSubSections]);

  // ------------------------------ Update Local Answer ------------------------------
  const updateLocalAnswer = useCallback((controlId, value) => {
    // ALWAYS work off the latest structure (via ref) — never the closure variable.
    // Rapid successive edits or an in-flight SignalR update can otherwise cause
    // this callback to compute its patch from a stale tree, silently dropping
    // the previous local edit.
    const currentStructure = structureRef.current;
    if (!currentStructure) return;

    // Find which subsection this control belongs to for action item modal auto-trigger
    let affectedSubsection = null;
    let affectedSection = null;
    traverseControls(currentStructure, (ctrl, sub, sec) => {
      if (ctrl.Id === controlId) {
        affectedSubsection = sub;
        affectedSection = sec;
      }
    });

    // No-op short-circuit: if the value is unchanged, skip the entire update
    // pipeline. This prevents spurious dirty-marks and SignalR broadcasts when
    // a control re-renders with the same value (e.g. radio re-selection).
    const existingCtrl = affectedSubsection?.Controls?.find(c => c.Id === controlId);
    if (existingCtrl && existingCtrl.value === value) {
      return;
    }

    const patched = sanitizeStructureTree({
      ...currentStructure,
      Structure: (currentStructure.Structure || []).map(cat => ({
        ...cat,
        Sections: (cat.Sections || []).map(sec => ({
          ...sec,
          SubSections: (sec.SubSections || []).map(sub => ({
            ...sub,
            Controls: (sub.Controls || []).map(ctrl =>
              ctrl.Id === controlId ? { ...ctrl, value } : ctrl
            )
          }))
        }))
      }))
    });
    structureRef.current = patched;
    setStructure(patched);

    // Persist answers to local storage
    const answersMap = {};
    traverseControls(patched, ctrl => {
      if (ctrl.value !== undefined && ctrl.value !== null) {
        answersMap[ctrl.Id] = ctrl.value;
      }
    });
    persistAnswersDebounced(answersMap);

    // Enqueue this single change as a delta and flush to server
    enqueueChange(controlId, value);
    flushDeltasToServer();

    // Recompute enforced subsections and check for new enforcements
    const previousEnforced = new Set(enforcedSubSections);
    const recomputed = recomputeEnforcedSubSections(patched);
    setEnforcedSubSections(recomputed);
    persistEnforcedSubSections(recomputed);

    // Auto-trigger action item modal for newly enforced subsections with no existing action items
    // Only trigger if the changed control is in the newly enforced subsection
    if (affectedSubsection && affectedSection) {
      const subId = affectedSubsection.Id;
      const wasNotEnforced = !previousEnforced.has(subId);
      const isNowEnforced = recomputed.has(subId);
      const hasNoActionItems = (actionCounts[subId] || 0) === 0;

      if (wasNotEnforced && isNowEnforced && hasNoActionItems && !editingDisabled) {
        // Delay slightly to allow UI to update, then open modal
        setTimeout(() => {
          // Find the selected option text for context
          const changedControl = affectedSubsection.Controls.find(c => c.Id === controlId);
          const selectedOptionText = changedControl?.Options?.find(o => o.Value === value)?.Text || '';

          openAiModal({
            subId: subId,
            subTitle: affectedSubsection.Title,
            sectionTitle: affectedSection.Title,
            controlLabel: changedControl?.Label || '',
            selectedValue: value,
            selectedOptionText: selectedOptionText
          });
        }, 300); // Short delay for smoother UX
      }
    }
    // Intentionally NOT depending on `structure` — we read structureRef.current
    // so this callback identity is stable across edits and we never act on a
    // stale snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistAnswersDebounced, enqueueChange, flushDeltasToServer, persistEnforcedSubSections, enforcedSubSections, actionCounts, editingDisabled, openAiModal]);

  // ------------------------------ UI Handlers ------------------------------
  const toggleCollapse = (id) => { if (id === 'all') { setCollapsed(prev => { const ids = []; (structure?.Structure || []).forEach(cat => (cat.Sections || []).forEach(sec => ids.push(sec.Id))); if (!ids.length) return prev; const anyExpanded = ids.some(x => !prev[x]); if (anyExpanded) { const next = {}; ids.forEach(x => { next[x] = true; }); return next; } return {}; }); return; } setCollapsed(c => ({ ...c, [id]: !c[id] })); };
  
  // Enhanced photo handling with error handling and size limits
  const handlePhotoChange = (controlId, file) => { 
    if (!file) { 
      updateLocalAnswer(controlId, null); 
      return; 
    } 
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select a valid image file.');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }
    
    // Check file size (limit to 5MB for better offline performance)
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setErrorMessage('Image file is too large. Please select an image smaller than 5MB.');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }
    
    const reader = new FileReader(); 
    reader.onload = e => { 
      updateLocalAnswer(controlId, e.target.result); 
    }; 
    reader.onerror = () => {
      setErrorMessage('Failed to read image file. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    };
    reader.readAsDataURL(file); 
  };
  
  const openPhotoModal = (src) => { setPhotoModalSrc(src); setPhotoModalOpen(true); }; 
  const closePhotoModal = () => { setPhotoModalOpen(false); setPhotoModalSrc(null); };

  const openCreateFromList = () => { if (!aiListTarget) return; openAiModal(aiListTarget); };

  // Handler for action item button click
  const handleActionItemButtonClick = useCallback((subId, subTitle, sectionTitle, actionCount) => {
    const subData = { subId, subTitle, sectionTitle };
    if (actionCount > 0) {
      openAiListModal(subData);
    } else {
      openAiModal(subData);
    }
  }, [openAiListModal, openAiModal]);

  // Action Item create - FIXED: Use useCallback for stable handlers
  const handleAiFormChange = useCallback((field, value) => {
    setAiForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const closeAiModal = useCallback(() => { 
    if (!aiSaving) setAiModalOpen(false); 
  }, [aiSaving]);

  const saveAi = useCallback(async () => { 
    if (!aiTarget) return; 
    if (!structure?.PropertyId && structure?.propertyId === undefined) { 
      closeAiModal(); 
      return; 
    } 
    setAiSaving(true); 
    try { 
      const propertyId = structure.PropertyId || structure.propertyId; 
      const payload = { 
        propertyId, 
        subSectionId: aiTarget.subId, 
        title: aiForm.title,
        description: aiForm.description, 
        sectionTitle: aiTarget.sectionTitle, 
        subTitle: aiTarget.subTitle, 
        priority: aiForm.priority, 
        comments: aiForm.comments,
        userId: user?.userID || user?.id || user?.Id || null,
        assignedToUserId: aiForm.assignedToUserId,
        sipaReportId: reportId ? parseInt(reportId, 10) : null
      }; 
      // Use the correct SipaCompletion endpoint for SIPA action items
      // This endpoint looks up the template from ModuleTemplateAssignments
      const res = await apiFetch('/api/SipaCompletion/sipa/action-item', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(payload) 
      }); 
      const data = await res.json().catch(() => null); 
      if (res.ok && data?.success) { 
        await fetchActionItemCounts(); 
        closeAiModal(); 
        if (aiListModalOpen && aiListTarget?.subId === aiTarget.subId) openAiListModal(aiListTarget); 
      } 
    } catch (catchError) { Sentry.captureException(catchError); } finally { 
      setAiSaving(false); 
    } 
  }, [aiTarget, structure, aiForm, apiFetch, closeAiModal, aiListModalOpen, aiListTarget, fetchActionItemCounts, openAiListModal]);
  // ------------------------------ Render Helpers ------------------------------
  const renderControlInput = (ctrl) => {
    const disabled = editingDisabled;
    const inputStyle = { width: '100%', maxWidth: '100%', boxSizing: 'border-box', fontSize: '0.8rem', padding: '6px8px', border: '1px solid #ccc', borderRadius: '3px', backgroundColor: disabled ? '#f0f0f0' : '#fff', fontFamily: 'inherit' };
    if (ctrl.Type === 'PhotoCapture' || /upload photo/i.test(ctrl.Label)) { const inputId = `photo_input_${ctrl.Id}`; return (<div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}><button type='button' disabled={disabled} onClick={() => { if (!disabled) { const el = document.getElementById(inputId); if (el) el.click(); } }} style={{ padding: '6px10px', fontSize: '0.75rem', background: '#4169e1', color: '#fff', border: 'none', borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '01px2px rgba(0,0,0,0.15)' }}>📷 Take Photo</button><input id={inputId} type='file' accept='image/*;capture=camera' disabled={disabled} onChange={e => handlePhotoChange(ctrl.Id, e.target.files?.[0])} style={{ position: 'absolute', left: '-9999px', width:1, height:1, opacity:0 }} />{ctrl.value && (<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><img src={ctrl.value} alt='Captured' onClick={() => openPhotoModal(ctrl.value)} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '2px solid #ddd', cursor: 'pointer' }} />{!disabled && (<button type='button' onClick={() => updateLocalAnswer(ctrl.Id, null)} style={{ padding: '4px8px', fontSize: '0.65rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Remove</button>)}</div>)}</div>); }
    if (ctrl.Type === 'TextMultiLine') return <textarea rows={2} disabled={disabled} value={ctrl.value || ''} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />;
    if (ctrl.Type === 'Number') return <input type='number' disabled={disabled} value={ctrl.value || ''} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} style={inputStyle} />;
    if (ctrl.Type === 'Checkbox') {
      // Segmented Yes/No control. A plain HTML checkbox can only express
      // "Yes" via a click — to record an explicit "No" the user previously
      // had to check the box and then uncheck it. With a Yes/No segmented
      // toggle, a single tap on either option always registers an answer.
      const allowNA = ctrl.AllowNotApplicable;
      const isYes = ctrl.value === 'true';
      const isNo = ctrl.value === 'false';
      const isNA = ctrl.value === 'NA';
      const segWrap = { display: 'inline-flex', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden', background: disabled ? '#f0f0f0' : '#fff', fontSize: '0.75rem' };
      const segBtn = (active, color) => ({
        padding: '6px 14px',
        border: 'none',
        background: active ? color : 'transparent',
        color: active ? '#fff' : '#444',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: active ? 600 : 400,
        borderRight: '1px solid #ccc',
        minWidth: '44px'
      });
      return (
        <div style={segWrap} role='group' aria-label={ctrl.Label}>
          <button type='button' disabled={disabled} aria-pressed={isYes}
            onClick={() => updateLocalAnswer(ctrl.Id, 'true')}
            style={segBtn(isYes, '#16a34a')}>Yes</button>
          <button type='button' disabled={disabled} aria-pressed={isNo}
            onClick={() => updateLocalAnswer(ctrl.Id, 'false')}
            style={{ ...segBtn(isNo, '#dc2626'), borderRight: allowNA ? '1px solid #ccc' : 'none' }}>No</button>
          {allowNA && (
            <button type='button' disabled={disabled} aria-pressed={isNA}
              onClick={() => updateLocalAnswer(ctrl.Id, 'NA')}
              style={{ ...segBtn(isNA, '#6b7280'), borderRight: 'none' }}>N/A</button>
          )}
        </div>
      );
    }
    if (ctrl.Type === 'RadioGroup') { const options = (ctrl.Options || []); const allowNA = ctrl.AllowNotApplicable; const hasNA = options.some(o => (o.Value ?? '').toUpperCase() === 'NA'); const name = `rg_${ctrl.Id}`; const wrap = { display: 'flex', flexWrap: 'wrap', gap: '6px14px', alignItems: 'center', padding: '6px8px', border: '1px solid #ccc', borderRadius: '4px', background: disabled ? '#f0f0f0' : '#fff', fontSize: '0.7rem' }; const opt = { display: 'flex', alignItems: 'center', gap:4, cursor: disabled ? 'not-allowed' : 'pointer' }; return (<div style={wrap} role='radiogroup' aria-label={ctrl.Label}>{options.map(o => (<label key={o.Id} style={opt}><input type='radio' name={name} value={o.Value} disabled={disabled} checked={ctrl.value === o.Value} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} /><span>{o.Text}</span></label>))}{allowNA && !hasNA && (<label style={opt}><input type='radio' name={name} value='NA' disabled={disabled} checked={ctrl.value === 'NA'} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} /><span>Not Applicable</span></label>)}{ctrl.value && (<button type='button' disabled={disabled} onClick={() => updateLocalAnswer(ctrl.Id, '')} style={{ marginLeft: 'auto', fontSize: '0.6rem', background: 'transparent', border: 'none', color: '#dc2626', cursor: disabled ? 'not-allowed' : 'pointer' }} title='Clear selection'>✕</button>)}</div>); }
    if (ctrl.Type === 'Dropdown') { const options = (ctrl.Options || []); const allowNA = ctrl.AllowNotApplicable; const hasNA = options.some(o => (o.Value ?? '').toUpperCase() === 'NA'); return (<select disabled={disabled} value={ctrl.value || ''} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} style={inputStyle}><option value=''>--</option>{allowNA && !hasNA && <option value='NA'>Not Applicable</option>}{options.map(o => <option key={o.Id} value={o.Value}>{o.Text}</option>)}</select>); }
    if (ctrl.Type === 'Date') return <input type='date' disabled={disabled} value={ctrl.value || ''} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} style={inputStyle} />;
    return <input disabled={editingDisabled} value={ctrl.value || ''} onChange={e => updateLocalAnswer(ctrl.Id, e.target.value)} style={inputStyle} />;
  };

  // Sign-off
  const openSignOff = () => { setSignOffAgree(false); setSignOffError(''); setSignOffModalOpen(true); ensureUsersLoaded(); };
  const closeSignOff = () => { if (!signing) setSignOffModalOpen(false); };
  const submitSignOff = async (overrideValidation = false) => {
    setSignOffError('');
    if (!signOffAgree) { setSignOffError('Please confirm declaration before submitting.'); return; }
    if (!overrideValidation && outstandingEnforced.length) { setSignOffError('Outstanding enforced subsections require action items.'); return; }
    if (!overrideValidation && incompleteScoringSubSections.length) { setSignOffError('All scoring controls must have an answer selected.'); return; }
    setSigning(true);
    try {
      const payload = { overrideValidation };
      const res = await apiFetch(`/api/sipa/reports/signoff/${reportId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => null);
      if (res.ok || res.status === 204) {
        const role = data?.role;
        if (role === 'HeadOfOperations') {
          setReportMeta(prev => ({ ...(prev||{}), finalSigned: true, centreManagerSigned: true }));
        } else {
          setReportMeta(prev => ({ ...(prev||{}), centreManagerSigned: true }));
        }
        closeSignOff();
      } else { setSignOffError(data?.message || data?.title || 'Sign-off failed'); }
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        setSignOffError('Sign-off error'); } finally { setSigning(false);
    }
  };

  // Revoke sign-off
  const openRevoke = (stage) => { setRevokeStage(stage); setRevokeReason(''); setRevokeError(''); setRevokeModalOpen(true); };
  const closeRevoke = () => { if (!revoking) setRevokeModalOpen(false); };
  const submitRevoke = async () => {
    if (!revokeReason.trim()) { setRevokeError('A reason is required.'); return; }
    setRevoking(true); setRevokeError('');
    try {
      const res = await apiFetch(`/api/sipa/reports/revoke-signoff/${reportId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage: revokeStage, reason: revokeReason }) });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        if (revokeStage === 'final') {
          setReportMeta(prev => ({ ...(prev||{}), finalSigned: false }));
        } else {
          setReportMeta(prev => ({ ...(prev||{}), centreManagerSigned: false }));
        }
        closeRevoke();
      } else { setRevokeError(data?.message || 'Revoke failed'); }
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        setRevokeError('Revoke error'); } finally { setRevoking(false);
    }
  };

  // Highlight helper (remove highlight after timeout)
  const triggerHighlight = (subId) => { setHighlightSubs(prev => { const next = new Set(prev); next.add(subId); return next; }); setTimeout(() => { setHighlightSubs(prev => { const next = new Set(prev); next.delete(subId); return next; }); }, 2500); };
  // Scroll to subsection ensuring its parent section is expanded first
  const scrollToSub = (subId) => {
    const sectionId = subToSectionIdMap[subId];
    if (sectionId) {
      setCollapsed(prev => {
        if (!prev[sectionId]) return prev; // already expanded
        const next = { ...prev }; delete next[sectionId]; return next; // expand by removing collapsed flag
      });
    }
    setTimeout(() => { const el = document.getElementById(`sub_${subId}`); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); triggerHighlight(subId); } }, 60);
  };
  // New helper: close modal then scroll
  const closeModalAndScrollToSub = (subId) => {
    closeSignOff();
    setTimeout(() => scrollToSub(subId), 80); // slight delay to allow modal removal & layout paint
  };

  // Filter users for search
  const filteredUsers = useMemo(() => {
    if (!aiUserSearch) return users;
    const lower = aiUserSearch.toLowerCase();
    return users.filter(u => {
      const name = u.name || u.fullName || u.displayName || '';
      const email = u.email || '';
      return name.toLowerCase().includes(lower) || email.toLowerCase().includes(lower);
    });
  }, [users, aiUserSearch]);

  // ------------------------------ Modal Components ------------------------------
  // Action Item Create Modal
  const ActionItemCreateModal = useMemo(() => {
    if (!aiModalOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px' }} onMouseDown={e => { if (e.target === e.currentTarget) closeAiModal(); }}>
        <div style={{ background: '#fff', borderRadius: '6px', width: '100%', maxWidth: '500px', boxShadow: '0px 4px 20px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Create Action Item</h3>
            <button onClick={closeAiModal} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 500 }}>Title</label>
              <input type="text" value={aiForm.title || ''} onChange={e => handleAiFormChange('title', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 500 }}>Description</label>
              <textarea value={aiForm.description || ''} onChange={e => handleAiFormChange('description', e.target.value)} rows={3} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 500 }}>Priority</label>
              <select value={aiForm.priority} onChange={e => handleAiFormChange('priority', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }}>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div style={{ marginBottom: '12px', position: 'relative' }} ref={assignToRef}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 500 }}>Assign To</label>
              <input 
                type="text" 
                value={aiUserSearch} 
                onChange={e => { setAiUserSearch(e.target.value); setShowUserSuggestions(true); }} 
                onFocus={() => setShowUserSuggestions(true)}
                placeholder="Search users..." 
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} 
              />
              {showUserSuggestions && filteredUsers.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ccc', borderRadius: '4px', maxHeight: '150px', overflow: 'auto', zIndex: 10 }}>
                  {filteredUsers.slice(0, 10).map(u => (
                    <div 
                      key={u.id || u.Id} 
                      onClick={() => { 
                        handleAiFormChange('assignedToUserId', u.id || u.Id); 
                        setAiUserSearch(u.name || u.fullName || u.email); 
                        setShowUserSuggestions(false); 
                      }} 
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.85rem' }}
                    >
                      {u.name || u.fullName || u.displayName || u.email}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 500 }}>Comments</label>
              <textarea value={aiForm.comments} onChange={e => handleAiFormChange('comments', e.target.value)} rows={3} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ padding: '12px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" onClick={closeAiModal} style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: 3, color: '#57565B' }}>Cancel</button>
            <button type="button" onClick={saveAi} disabled={aiSaving} style={{ padding: '8px 20px', border: 'none', background: '#dc2626', color: '#fff', borderRadius: 3 }}>{aiSaving ? 'Creating...' : 'Create Action Item'}</button>
          </div>
        </div>
      </div>
    );
  }, [aiModalOpen, aiForm, aiUserSearch, showUserSuggestions, filteredUsers, aiSaving, closeAiModal, handleAiFormChange, saveAi]);

  // Action Items List Modal
  const ActionItemsListModalContent = useMemo(() => {
    if (!aiListModalOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px' }} onMouseDown={e => { if (e.target === e.currentTarget) closeAiListModal(); }}>
        <div style={{ background: '#fff', borderRadius: '6px', width: '100%', maxWidth: '600px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0px 4px 20px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Action Items: {aiListTarget?.subTitle || 'Subsection'}</h3>
            <button onClick={closeAiListModal} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
            {aiListLoading && <div style={{ textAlign: 'center', color: '#666' }}>Loading...</div>}
            {!aiListLoading && aiListItems.length === 0 && <div style={{ textAlign: 'center', color: '#666' }}>No action items for this subsection.</div>}
            {!aiListLoading && aiListItems.map(item => (
              <div 
                key={item.id || item.Id} 
                onClick={() => window.open(`/action-items/${item.id || item.Id}`, '_blank')}
                style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '8px', cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
              >
                <div style={{ fontWeight: 500, marginBottom: '4px' }}>{item.description || 'No description'}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>Priority: {item.priority || 'Low'} | Status: {item.status || 'Open'}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" onClick={closeAiListModal} style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: 3, color: '#333', fontSize: '0.85rem', cursor: 'pointer' }}>Close</button>
            {!editingDisabled && <button type="button" onClick={openCreateFromList} style={{ padding: '8px 16px', border: 'none', background: '#dc2626', color: '#fff', borderRadius: 3, fontSize: '0.85rem', cursor: 'pointer' }}>Create New</button>}
          </div>
        </div>
      </div>
    );
  }, [aiListModalOpen, aiListTarget, aiListLoading, aiListItems, editingDisabled, closeAiListModal, openCreateFromList]);

  // Photo Modal
  const PhotoModalContent = useMemo(() => {
    if (!photoModalOpen || !photoModalSrc) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '20px' }} onClick={closePhotoModal}>
        <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }}>
          <img src={photoModalSrc} alt="Full size" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '4px' }} />
          <button onClick={closePhotoModal} style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#fff', border: 'none', borderRadius: '50%', width: '30px', height: '30px', fontSize: '1rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>✕</button>
        </div>
      </div>
    );
  }, [photoModalOpen, photoModalSrc, closePhotoModal]);

  // Sign-Off Modal
  const SignOffModalContent = useMemo(() => {
    if (!signOffModalOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px' }} onMouseDown={e => { if (e.target === e.currentTarget) closeSignOff(); }}>
        <div style={{ background: '#fff', borderRadius: '6px', width: '100%', maxWidth: '550px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0px 4px 20px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Final Sign-Off</h3>
            <button onClick={closeSignOff} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {outstandingEnforced.length > 0 && (
              <div style={{ marginBottom: '16px', background: '#fff4e6', border: '1px solid #f59e0b', borderRadius: 4, padding: '12px' }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>Outstanding Action Items Required:</div>
                {outstandingEnforced.map(subId => (
                  <div key={subId} style={{ fontSize: '0.85rem', color: '#92400e', marginBottom: '4px', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => closeModalAndScrollToSub(subId)}>
                    {subDisplayMap[subId] || subId}
                  </div>
                ))}
              </div>
            )}
            {incompleteScoringSubSections.length > 0 && (
              <div style={{ marginBottom: '16px', background: '#eef2ff', border: '1px solid #6366f1', borderRadius: 4, padding: '12px' }}>
                <div style={{ fontWeight: 600, color: '#3730a3', marginBottom: '8px' }}>Incomplete Scoring Subsections:</div>
                {incompleteScoringSubSections.map(subId => (
                  <div key={subId} style={{ fontSize: '0.85rem', color: '#3730a3', marginBottom: '4px', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => closeModalAndScrollToSub(subId)}>
                    {subDisplayMap[subId] || subId}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={signOffAgree} onChange={e => setSignOffAgree(e.target.checked)} style={{ marginTop: '4px' }} />
                <span style={{ fontSize: '0.9rem' }}>I declare that I have reviewed this Site Performance Assessment report and confirm that all information provided is accurate and complete to the best of my knowledge.</span>
              </label>
            </div>
            {signOffError && (
              <div style={{ marginBottom: '12px', background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 4, fontSize: '0.85rem' }}>{signOffError}</div>
            )}
          </div>
          <div style={{ padding: '12px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" onClick={closeSignOff} style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: 3, color: '#F63040' }}>Cancel</button>
            {canOverrideValidation && (outstandingEnforced.length > 0 || incompleteScoringSubSections.length > 0) && (
              <button type="button" onClick={() => submitSignOff(true)} disabled={signing || !signOffAgree} style={{ padding: '8px 20px', border: 'none', background: signing || !signOffAgree ? '#9ca3af' : '#f59e0b', color: '#fff', borderRadius: 3, cursor: signing || !signOffAgree ? 'not-allowed' : 'pointer', fontWeight: 600 }}>{signing ? 'Signing...' : 'Override & Sign Off'}</button>
            )}
            <button type="button" onClick={() => submitSignOff(false)} disabled={signing || !signOffAgree} style={{ padding: '8px 20px', border: 'none', background: signing || !signOffAgree ? '#9ca3af' : '#dc2626', color: '#fff', borderRadius: 3, cursor: signing || !signOffAgree ? 'not-allowed' : 'pointer' }}>{signing ? 'Signing...' : 'Sign Off'}</button>
          </div>
        </div>
      </div>
    );
  }, [signOffModalOpen, signOffAgree, signOffError, signing, canOverrideValidation, outstandingEnforced, incompleteScoringSubSections, subDisplayMap, closeSignOff, submitSignOff, closeModalAndScrollToSub]);

  // ------------------------------ Main Render ------------------------------
  return (
    <div style={{ background: '#e8ebed', padding: 0 }}>
      {/* Error Message Banner */}
      {errorMessage && (
        <div style={{ 
          background: '#fee2e2', 
          borderBottom: '2px solid #ef4444', 
          padding: '10px 20px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: '#991b1b'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            <span>{errorMessage}</span>
          </div>
          <button 
            onClick={() => setErrorMessage('')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#991b1b',
              cursor: 'pointer',
              fontSize: '1.2rem',
              padding: '0 8px'
            }}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Offline Banner */}
      {(!isOnline || pendingSync) && (
        <div style={{ 
          background: !isOnline ? '#fee2e2' : '#fff7ed', 
          borderBottom: '2px solid ' + (!isOnline ? '#ef4444' : '#f59e0b'), 
          padding: '10px 20px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '12px',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: !isOnline ? '#991b1b' : '#92400e'
        }}>
          <span style={{ fontSize: '1.2rem' }}>{!isOnline ? '⚠️' : '🔄'}</span>
          <span>
            {!isOnline 
              ? 'You are currently offline. Your changes are being saved locally and will sync when connection is restored.' 
              : `${getQueue().length} change(s) pending sync. Your work is safe.`}
          </span>
          {isOnline && pendingSync && (
            <button 
              onClick={processQueue}
              style={{
                padding: '4px 12px',
                fontSize: '0.75rem',
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Retry Sync Now
            </button>
          )}
        </div>
      )}
      
      {/* Header */}
      <div style={{ background: '#d3dce6', borderBottom: '1px solid #b8c2cc', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 400, color: '#4169e1' }}>{headerLabel}</h1>
          <button onClick={() => toggleCollapse('all')} style={{ background: 'transparent', border: 'none', color: '#4169e1', cursor: 'pointer', fontSize: '1.2rem', padding: '0 8px' }} title={Object.keys(collapsed).length ? 'Expand All' : 'Collapse All'}>
            {Object.keys(collapsed).length ? '▸' : '▾'}
          </button>
          
          {/* Active Users Indicator (Real-Time Collaboration) */}
          {activeUsers.length > 0 && (
            <div 
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#5a6c7d', background: '#fff', padding: '4px 10px', borderRadius: '12px', border: '1px solid #b8c2cc', cursor: 'help' }}
              title={activeUsers.map(u => {
                // Extract name from various possible properties
                const name = u.UserName || u.userName;
                if (name && !name.includes('@')) {
                  return name; // Already a proper name
                }
                // If it's an email, try to extract name from it
                const email = u.UserEmail || u.userEmail || name;
                if (email && email.includes('@')) {
                  const localPart = email.split('@')[0];
                  // Convert snake_case or dot.case to readable name
                  return localPart.split(/[._]/).map(part => 
                    part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
                  ).join(' ');
                }
                return 'Unknown User';
              }).join(', ')}
            >
              <span style={{ fontSize: '1rem' }}>👥</span>
              <span>{activeUsers.length} other user{activeUsers.length !== 1 ? 's' : ''} editing</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.75rem', color: '#5a6c7d' }}>
          <span>{status}</span>
          {autoSaveTS && <span>Saved {autoSaveTS.toLocaleTimeString()}</span>}
          {/* Real-Time Sync Indicator */}
          {signalRConnected && (
            <span style={{ 
              color: '#10b981',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              title: 'Real-time collaboration active'
            }}>
              <span style={{ fontSize: '0.6rem' }}>●</span>
              Live Sync
            </span>
          )}
          <span style={{ 
            color: isOnline ? '#22c55e' : '#ef4444',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontSize: '0.6rem' }}>●</span>
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {finalSigned && <span style={{ background: '#d1fae5', color: '#065f46', padding: '4px 8px', borderRadius: '4px', fontWeight: 600 }}>Final Signed Off</span>}
          {!finalSigned && centreManagerSigned && <span style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 8px', borderRadius: '4px', fontWeight: 600 }}>Primary Signed Off</span>}
        </div>
      </div>

      {/* Sections */}
      {structure && sectionRows.length > 0 && (
        <div style={{ padding: 0 }}>
          {sectionRows.map((sec, idx) => {
            const stats = calcSectionStats(sec); const collapsedState = collapsed[sec.Id]; const fullyNA = isSectionFullyNA(sec);
            const canNA = sectionSupportsNA(sec); // whether switch should be shown/enabled
            const isAllNA = sectionIsAllNA(sec);
            return (
              <div key={sec.Id} style={{ marginBottom: 0, border: '1px solid #ccc', borderTop: idx === 0 ? '1px solid #ccc' : 'none', opacity: fullyNA ? 0.65 : 1 }}>
                <div onClick={() => toggleCollapse(sec.Id)} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 0, background: 'linear-gradient(to bottom, #e74c5a 0%, #e62639 50%, #c91f2e 100%)', color: '#fff', padding: '12px 16px', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
                  <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div>{idx + 1}. {sec.Title} {fullyNA && <span style={{ fontSize: '0.65rem', fontWeight: 400, marginLeft: 8, background: '#fff', color: '#c91f2e', padding: '2px 6px', borderRadius: 4 }}>ALL NA (Excluded)</span>}</div>

                    {/* Section-level "Mark All NA" switch — only shown if every scoring control supports NA */}
                    {canNA && (
                      <label
                        onClick={(e) => e.stopPropagation()} /* prevent parent collapse toggle */
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: isAllNA ? '#ffffff22' : '#ffffff11', borderRadius: 4, padding: '4px 6px', cursor: 'pointer', fontSize: '0.75rem' }}
                        title={isAllNA ? 'All scoring controls are Not Applicable' : 'Mark all scoring controls in this section as Not Applicable'}
                      >
                        <input
                          type="checkbox"
                          checked={isAllNA}
                          disabled={editingDisabled}
                          onChange={(e) => setSectionNotApplicable(sec.Id, e.target.checked)}
                          style={{ width: 14, height: 14, cursor: editingDisabled ? 'not-allowed' : 'pointer' }}
                        />
                        <span style={{ color: '#fff', opacity: 0.95 }}>{isAllNA ? 'All NA' : 'Mark section NA'}</span>
                      </label>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', minWidth: '100px' }}><div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Score</div><div style={{ fontWeight: 600 }}>{stats.avg ? `${stats.avg} %` : '-'}</div></div>
                  <div style={{ textAlign: 'center', minWidth: '100px' }}><div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Weight</div><div style={{ fontWeight: 600 }}>{(sec.Weight ?? 0)} %</div></div>
                  <div style={{ textAlign: 'center', minWidth: '120px' }}><div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Final Score</div><div style={{ fontWeight: 600 }}>{stats.avg && sec.Weight ? `${(parseFloat(stats.avg) * (sec.Weight / 100)).toFixed(2)} %` : '-'}</div></div>
                  <div style={{ textAlign: 'center', fontSize: '1.2rem', width: '40px' }}>{collapsedState ? '▸' : '▾'}</div>
                </div>
                {!collapsedState && (
                  <div>
                    {(sec.SubSections || []).map((sub, subIdx) => {
                      const controls = sub.Controls || []; const actionCount = actionCounts[sub.Id] || 0; const isEnforced = enforcedSubSections.has(sub.Id); const needsAction = isEnforced && actionCount === 0; const rowBg = subIdx % 2 === 0 ? '#f5f5f5' : '#e0e0e0'; const highlighted = highlightSubs.has(sub.Id); const missingScoring = incompleteScoringSubSections.includes(sub.Id);
                      return (
                        <div key={sub.Id} id={`sub_${sub.Id}`} style={{ background: rowBg, padding: '10px 0 0 0', borderTop: '1px solid #ccc', boxShadow: highlighted ? '0 0 0 3px #f59e0b inset' : 'none', transition: 'box-shadow 0.3s' }}>
                          <div style={{ padding: '0 16px 10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {idx + 1}.{subIdx + 1} {sub.Title}
                              {isEnforced && (
                                <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: needsAction ? '#fff4e6' : '#fee2e2', color: needsAction ? '#c2410c' : '#991b1b', borderRadius: '3px', border: needsAction ? '1px solid #f59e0b' : 'none', fontWeight: 500 }}>{needsAction ? 'Missing Action Item' : 'Action Enforced'}</span>
                              )}
                              {missingScoring && (
                                <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: '#eef2ff', color: '#3730a3', borderRadius: '3px', border: '1px solid #6366f1', fontWeight: 500 }}>Incomplete Scoring</span>
                              )}
                            </div>
                            <button 
                              type='button' 
                              disabled={templateLoading || editingDisabled}
                              onClick={() => handleActionItemButtonClick(sub.Id, sub.Title, sec.Title, actionCount)} 
                              style={{ fontSize: '0.75rem', background: templateLoading ? '#9ca3af' : (actionCount ? '#dc2626' : '#fff'), color: templateLoading ? '#fff' : (actionCount ? '#fff' : '#333'), padding: '6px 14px', borderRadius: '3px', cursor: (templateLoading || editingDisabled) ? 'not-allowed' : 'pointer', border: actionCount ? 'none' : '1px solid #ccc', opacity: editingDisabled && !actionCount ? 0.5 : 1 }}
                            >
                              {templateLoading ? 'Loading...' : (actionCount ? `${actionCount} Action Items` : 'Create')}
                            </button>
                          </div>
                          <div style={{ padding: '0 16px 12px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '12px' }}>
                            {controls.map(ctrl => (
                              <div key={ctrl.Id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#555', marginBottom: 2 }}>
                                  {ctrl.Label}
                                  {ctrl.ExcludeFromScore && <span style={{ fontSize: '0.65rem', color: '#888', fontStyle: 'italic' }}> (Info only)</span>}
                                </label>
                                {renderControlInput(ctrl)}
                              </div>
                            ))}
                          </div>
                          {needsAction && (
                            <div style={{ margin: '0 16px 12px 16px', background: '#fff7ed', border: '1px solid #f59e0b', borderRadius: 4, padding: '6px 10px', fontSize: '0.65rem', color: '#92400e' }}>
                              An enforced selection was made in this subsection but no Action Item exists yet. Please create one before final sign-off.
                          </div>
                          )}
                          {missingScoring && (
                            <div style={{ margin: '0 16px 12px 16px', background: '#eef2ff', border: '1px solid #6366f1', borderRadius: 4, padding: '6px 10px', fontSize: '0.65rem', color: '#3730a3' }}>
                              One or more scoring controls in this subsection are missing an answer. Please select a value (or NA where applicable).
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* FIXED: Ensured consistent spacing for subsections */}
                    <div style={{ height: '12px' }}></div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Final category summary bar */}
          {categoryScores.length > 0 && (
            <div style={{ marginTop: 0, border: '1px solid #ccc', borderTop: 'none' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', background: 'linear-gradient(to bottom, #e74c5a 0%, #e62639 50%, #c91f2e 100%)', color: '#fff', padding: '14px 20px', alignItems: 'center', fontSize: '0.85rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginRight: '12px' }}>Category Scores</div>
                {categoryScores.map((c, i) => (
                  <div key={i} style={{ minWidth: '140px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '0.65rem', opacity: 0.9 }}>{c.name}</span>
                    <span style={{ fontWeight: 600 }}>{c.score !== null ? `${c.score.toFixed(2)} %` : '-'}</span>
                  </div>
                ))}
                <div style={{ minWidth: '160px', marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontSize: '0.65rem', opacity: 0.9 }}>Overall</span>
                  <span style={{ fontWeight: 700 }}>{overallScoreInfo.score !== null ? `${overallScoreInfo.score} %` : '-'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {structure && sectionRows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#6b7280' }}>No active sections in the current design.</div>
      )}

      {/* Sign-off / Revert / Revoke buttons */}
      {canFinalSignOff && !finalSigned && !(centreManagerSigned && isPrimarySigner) && (
        <div style={{ padding: '30px 20px', textAlign: 'center' }}>
          <button type='button' onClick={openSignOff} style={{ padding: '12px 28px', fontSize: '0.9rem', background: (outstandingEnforced.length || incompleteScoringSubSections.length) ? '#f59e0b' : '#dc2626', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', minWidth: '240px' }}>
            {(outstandingEnforced.length || incompleteScoringSubSections.length) ? 'Outstanding Items - Review Before Sign Off' : centreManagerSigned ? `Final Sign Off as ${workflowPerms.roleName || userRoleDisplay || 'Authorized User'}` : `Sign Off as ${workflowPerms.roleName || userRoleDisplay || 'Authorized User'}`}
          </button>
          {(outstandingEnforced.length || incompleteScoringSubSections.length) > 0 && <div style={{ marginTop: 10, fontSize: '0.7rem', color: '#92400e' }}>Resolve enforced action items and complete all scoring answers before sign-off.</div>}
        </div>
      )}

      {/* Revert primary sign-off: secondary approvers can revert when primary is signed but not final */}
      {centreManagerSigned && !finalSigned && (workflowPerms.canSecondarySignoff || workflowPerms.canOverrideValidation) && (
        <div style={{ padding: '10px 20px', textAlign: 'center' }}>
          <button type='button' onClick={() => openRevoke('primary')} style={{ padding: '10px 24px', fontSize: '0.85rem', background: '#fff', color: '#dc2626', border: '2px solid #dc2626', borderRadius: '5px', cursor: 'pointer', minWidth: '200px' }}>
            Revert Primary Approval
          </button>
        </div>
      )}

      {/* Revoke final sign-off: override roles only */}
      {finalSigned && workflowPerms.canOverrideValidation && (
        <div style={{ padding: '10px 20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button type='button' onClick={() => openRevoke('final')} style={{ padding: '10px 24px', fontSize: '0.85rem', background: '#fff', color: '#dc2626', border: '2px solid #dc2626', borderRadius: '5px', cursor: 'pointer' }}>
            Revoke Final Approval
          </button>
        </div>
      )}

      {ActionItemCreateModal}
      {ActionItemsListModalContent}
      {PhotoModalContent}
      {SignOffModalContent}
      {/* Revoke Sign-Off Modal */}
      {revokeModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px' }} onMouseDown={e => { if (e.target === e.currentTarget) closeRevoke(); }}>
          <div style={{ background: '#fff', borderRadius: '6px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0px 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{revokeStage === 'final' ? 'Revoke Final Approval' : 'Revert Primary Approval'}</h3>
              <button type="button" aria-label="Close revoke dialog" title="Close revoke dialog" onClick={closeRevoke} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: 12 }}>
                {revokeStage === 'final'
                  ? 'This will remove the final sign-off and reopen the report for the secondary approver.'
                  : 'This will remove the primary sign-off and reopen the report for editing.'}
              </p>
              <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: 6 }}>Reason for revocation *</label>
              <textarea rows={3} value={revokeReason} onChange={e => setRevokeReason(e.target.value)} placeholder="Please provide a reason..." style={{ width: '100%', boxSizing: 'border-box', padding: '8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.85rem', resize: 'vertical' }} />
              {revokeError && <div style={{ marginTop: 8, background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 4, fontSize: '0.85rem' }}>{revokeError}</div>}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" onClick={closeRevoke} style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: 3, color: '#333' }}>Cancel</button>
              <button type="button" onClick={submitRevoke} disabled={revoking || !revokeReason.trim()} style={{ padding: '8px 20px', border: 'none', background: revoking || !revokeReason.trim() ? '#9ca3af' : '#dc2626', color: '#fff', borderRadius: 3, cursor: revoking || !revokeReason.trim() ? 'not-allowed' : 'pointer', fontWeight: 600 }}>{revoking ? 'Revoking...' : 'Confirm Revocation'}</button>
            </div>
          </div>
        </div>
      )}
      {TemplateModal}
    </div>
  );
};

export default SipaReportCapturePage;